window.PXUTheme = window.PXUTheme || {};

// ==========================================================================
//  SubscriptionBlockController
//  Per-block controller for the global subscription block.
//  Instances are created by jsGlobalProduct.initSubscriptionBlocks() from
//  config objects pushed to window._subscriptionBlockQueue by the inline
//  Liquid script in p_global_subscription.liquid.
// ==========================================================================
class SubscriptionBlockController {
  constructor(blockEl, config) {
    this.blockEl = blockEl;
    this.blockId = blockEl.id;
    this.config = config;

    // Flags from data-* attributes
    this.defaultSelection = blockEl.dataset.defaultSelection || 'none';
    this.isSellingPlanOnly = blockEl.dataset.sellingPlanOnly === 'true';
    this.useColorMatch = blockEl.dataset.colorMatch === 'true';
    this.isAddon = blockEl.dataset.isAddon === 'true';
    this.hideNoSub = blockEl.dataset.hideNoSub === 'true';

    // Liquid-injected values passed from inline script
    this.subscribeAtcText = config.subscribeAtcText || '';
    this.variantColorMap = config.variantColorMap || {};

    this.hasAdditionalOffer = blockEl.dataset.additionalOffer === 'true';
    this.originalAtcLabel = null;
    this._cleanup = [];

    // Variant → plan filtering (SPO mode)
    this._variantPlanMap = null;
    this._lastVariantId = null;
    this._lastHadPlans = undefined;
    if (this.isSellingPlanOnly && blockEl.dataset.variantPlanLinks) {
      this._variantPlanMap = this._parseVariantPlanLinks(
        blockEl.dataset.variantPlanLinks,
        blockEl.dataset.universalPlanIndices || ''
      );
      // Seed with the Liquid-rendered initial variant so onVariantChanged
      // skips redundant DOM work when JS detects the same variant on init.
      const ssrVid = blockEl.dataset.initialVariantId;
      if (ssrVid) {
        this._lastVariantId = String(ssrVid);
        const ssrAllowed = this._variantPlanMap[this._lastVariantId]
          || this._variantPlanMap._universal || [];
        this._lastHadPlans = ssrAllowed.length > 0;
      }
    }
  }

  // ------------------------------------------------------------------
  //  Selection State
  // ------------------------------------------------------------------

  getFieldset() {
    return this.blockEl.querySelector('fieldset') || this.blockEl.querySelector('.global-product__plan');
  }

  getCheckedInput() {
    if (this.isSellingPlanOnly) {
      return this.blockEl.querySelector('input[name="selling_plan_option"]:checked');
    }
    return this.blockEl.querySelector('input[name="subscription-plan"]:checked');
  }

  isSubscriptionChecked() {
    const checked = this.getCheckedInput();
    const primarySub = checked && !checked.classList.contains('no-subscription-input') &&
      !(this.isSellingPlanOnly && checked.id === this.blockId + '-buy_once');
    if (primarySub) return true;
    if (this.hasAdditionalOffer) {
      const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
      if (addlInput && addlInput.checked) return true;
    }
    return false;
  }

  updateSelectionState() {
    const fieldset = this.getFieldset();
    if (!fieldset) return;

    const hasSelection = this.getCheckedInput();
    if (hasSelection) {
      fieldset.setAttribute('data-sticky-selection', 'selected');
      fieldset.setAttribute('data-selection', 'selected');
      fieldset.classList.remove('not-loaded');
      this.blockEl.setAttribute('data-sticky-selection', 'selected');
      this.blockEl.setAttribute('data-selection', 'selected');
    } else {
      fieldset.removeAttribute('data-sticky-selection');
      fieldset.removeAttribute('data-selection');
      fieldset.setAttribute('data-sticky-selection', '');
      fieldset.classList.remove('not-loaded');
    }

    this.updateAtcText();

    if (window.PXUTheme?.jsGlobalProduct?.updateFloatingATC) {
      window.PXUTheme.jsGlobalProduct.updateFloatingATC();
    }
  }

  // ------------------------------------------------------------------
  //  ATC Button Text
  // ------------------------------------------------------------------

  applyAtcLabel(label) {
    const atcBtn = document.querySelector('[data-product-atc]');
    if (!atcBtn) return;
    atcBtn.setAttribute('data-label-add-to-cart', label);
    atcBtn.textContent = label;

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp) {
      if (gp.buttonLabels) {
        gp.buttonLabels.addToCart = label;
        gp.buttonLabels.floatingAddToCart = label;
      }
      if (gp.updateFloatingATC) gp.updateFloatingATC();
    }
  }

  updateAtcText() {
    if (!this.subscribeAtcText) return;

    const atcBtn = document.querySelector('[data-product-atc]');
    if (!atcBtn) return;

    if (this.originalAtcLabel === null) {
      this.originalAtcLabel = atcBtn.getAttribute('data-label-add-to-cart') || atcBtn.textContent.trim();
    }

    const label = this.isSubscriptionChecked() ? this.subscribeAtcText : this.originalAtcLabel;
    this.applyAtcLabel(label);
    setTimeout(() => this.applyAtcLabel(label), 0);
  }

  // ------------------------------------------------------------------
  //  Main Product Skip (add-item / replace mode)
  // ------------------------------------------------------------------

  updateMainProductSkip() {
    if (this.isSellingPlanOnly) return;

    const subProduct = this.blockEl.querySelector('product[data-replaces-main]');
    if (!subProduct) return;

    const parent = this.blockEl.closest('.global-product');
    if (!parent) return;

    const mainProduct = parent.querySelector('product[data-variant-picker]');
    if (!mainProduct) return;

    if (this.isSubscriptionChecked()) {
      mainProduct.dataset.skipForSubscription = 'true';
      delete subProduct.dataset.skipForSubscription;
    } else {
      delete mainProduct.dataset.skipForSubscription;
    }
  }

  // ------------------------------------------------------------------
  //  Selling Plan ATC (selling_plan_only mode)
  // ------------------------------------------------------------------

  updateSellingPlanOnATC() {
    if (!this.isSellingPlanOnly) return;

    const buyPlan = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    const dropdown = this.blockEl.querySelector('select[name="selling_plan_select"]');
    const sellingPlanInput = this.blockEl.querySelector('input[name="selling_plan"]');
    const form = this.blockEl.closest('form');
    const qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    const atcButtons = document.querySelectorAll('[data-product-atc]');
    let sellingPlanId = '';
    let planQty = '1';

    if (buyPlan && buyPlan.checked) {
      if (dropdown) {
        let selectedOpt = dropdown.options[dropdown.selectedIndex];
        if (selectedOpt && (selectedOpt.hidden || selectedOpt.disabled)) {
          selectedOpt = Array.from(dropdown.options).find(o => !o.hidden && !o.disabled) || null;
          if (selectedOpt) dropdown.value = selectedOpt.value;
        }
        sellingPlanId = selectedOpt ? (selectedOpt.dataset.sellingPlanId || '') : '';
        planQty = selectedOpt ? (selectedOpt.dataset.quantity || '1') : '1';
      } else {
        sellingPlanId = this.blockEl.dataset.firstSellingPlanId || '';
        const subRadio = this.blockEl.querySelector('input[name="subscription-plan"]');
        if (subRadio && subRadio.dataset.quantity) {
          planQty = subRadio.dataset.quantity.split(',')[0] || '1';
        }
      }
      if (sellingPlanInput) sellingPlanInput.value = sellingPlanId;
      if (qtyInput) qtyInput.value = planQty;
    } else {
      if (sellingPlanInput) sellingPlanInput.value = '';
      if (qtyInput) qtyInput.value = '1';
    }

    atcButtons.forEach(btn => btn.setAttribute('data-selling-plan', sellingPlanId));
  }

  // ------------------------------------------------------------------
  //  Price Display
  // ------------------------------------------------------------------

  updateVisibleVariantPrices(variantId) {
    if (!variantId) return;
    const vid = String(variantId);
    this.blockEl.querySelectorAll('.price[data-variant-id], [data-plan-row-price] [data-variant-id]').forEach(el => {
      el.style.display = el.dataset.variantId === vid ? '' : 'none';
    });
    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();
  }

  updateAddItemBoxPrice() {
    if (this.isSellingPlanOnly) return;
    let activeSubInput = null;
    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      if (activeSubSet) activeSubInput = activeSubSet.querySelector('input[name="subscription-plan"]');
    } else {
      activeSubInput = this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
    }
    if (!activeSubInput) return;

    const priceVal = Number(activeSubInput.dataset.price || 0);
    const compareVal = Number(activeSubInput.dataset.comparePrice || 0);
    const qtyStr = activeSubInput.dataset.quantity || '1';
    const qty = Number(qtyStr.split(',')[0]) || 1;
    const mainPrice = priceVal * qty;
    const mainCompare = compareVal * qty;
    let totalPrice = mainPrice;
    let totalCompare = mainCompare;

    const comboPrice = Number(activeSubInput.dataset.comboPrice || 0);
    const comboCompare = Number(activeSubInput.dataset.comboCompare || comboPrice);
    const comboQty = Number(activeSubInput.dataset.comboQty || 0);
    let comboTotalPrice = 0;
    let comboTotalCompare = 0;
    if (comboPrice > 0 && comboQty > 0) {
      comboTotalPrice = comboPrice * comboQty;
      comboTotalCompare = comboCompare * comboQty;
      totalPrice += comboTotalPrice;
      totalCompare += comboTotalCompare;
    }
    const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';

    const label = activeSubInput.closest('.sub-colour-set') || activeSubInput.parentElement;
    if (!label) return;
    const priceDiv = label.querySelector('.price') || (activeSubInput.nextElementSibling && activeSubInput.nextElementSibling.querySelector('.price'));
    if (!priceDiv) return;

    let totalHtml = Shopify.formatMoney(totalPrice, moneyFormat);
    if (totalCompare > totalPrice) {
      totalHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(totalCompare, moneyFormat) + '</span>';
    }
    priceDiv.innerHTML = totalHtml;

    let mainHtml = Shopify.formatMoney(mainPrice, moneyFormat);
    if (mainCompare > mainPrice) {
      mainHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(mainCompare, moneyFormat) + '</span>';
    }

    const deliveryColPrice = label.querySelector('[data-delivery-col-price]');
    if (deliveryColPrice) {
      deliveryColPrice.innerHTML = (comboTotalPrice > 0 ? mainHtml : totalHtml);
    }

    this.blockEl.querySelectorAll('[data-plan-row-price]').forEach(rowSlot => {
      rowSlot.innerHTML = '<span>' + mainHtml + '</span>';
    });

    if (comboTotalPrice > 0) {
      let comboHtml = Shopify.formatMoney(comboTotalPrice, moneyFormat);
      if (comboTotalCompare > comboTotalPrice) {
        comboHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(comboTotalCompare, moneyFormat) + '</span>';
      }
      this.blockEl.querySelectorAll('[data-combo-row-price]').forEach(rowSlot => {
        rowSlot.innerHTML = '<span>' + comboHtml + '</span>';
      });
      const comboDeliveryPrice = this.blockEl.querySelector('[data-combo-delivery-col-price]');
      if (comboDeliveryPrice) {
        comboDeliveryPrice.innerHTML = comboHtml;
      }
    }

    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();
  }

  syncCompactPriceInPlanRow() {
    if ((this.blockEl.dataset.layout || '') !== 'compact') return;

    const activeVariantId = this._getCurrentVariantId();

    this.blockEl.querySelectorAll('[data-plan-row-price]').forEach(slot => {
      const variantSpans = slot.querySelectorAll('[data-variant-id]');
      if (variantSpans.length > 0 && activeVariantId) {
        variantSpans.forEach(span => {
          span.style.display = span.dataset.variantId === String(activeVariantId) ? '' : 'none';
        });
      }
    });
  }

  updatePricePerDay() {
    this.blockEl.querySelectorAll('[data-show-price-per-day]').forEach(columns => {
      const perDayEl = columns.querySelector('[data-price-per-day]');
      if (!perDayEl) return;

      const isCombo = columns.hasAttribute('data-combo-delivery-columns');
      const intervalEl = isCombo
        ? columns.querySelector('[data-combo-delivery-col-value]')
        : columns.querySelector('[data-delivery-col-value]');
      const intervalText = (intervalEl?.textContent || '').trim().toLowerCase();
      const days = this._intervalToDays(intervalText);
      if (!days) { perDayEl.textContent = ''; return; }

      let priceCents = 0;
      const colPrice = isCombo
        ? columns.querySelector('[data-combo-delivery-col-price]')
        : columns.querySelector('[data-delivery-col-price]');
      if (colPrice) {
        priceCents = this._extractPriceCents(colPrice);
      } else {
        const visiblePrice = columns.querySelector('.price[data-variant-id]:not([style*="display: none"]):not([style*="display:none"])');
        if (visiblePrice) priceCents = this._extractPriceCents(visiblePrice);
      }

      if (!priceCents) { perDayEl.textContent = ''; return; }

      const perDay = priceCents / days;
      const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';
      perDayEl.textContent = '(' + Shopify.formatMoney(Math.round(perDay), moneyFormat) + '/day)';
    });
  }

  _intervalToDays(text) {
    const match = text.match(/every\s+(\d+)\s+(day|week|month|year)s?/i);
    if (!match) return 0;
    const count = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    if (unit === 'day') return count;
    if (unit === 'week') return count * 7;
    if (unit === 'month') return count * 30;
    if (unit === 'year') return count * 365;
    return 0;
  }

  _extractPriceCents(el) {
    const clone = el.cloneNode(true);
    const compareAt = clone.querySelector('.compare-at-price');
    if (compareAt) compareAt.remove();
    const text = clone.textContent.replace(/[^0-9.,]/g, '').trim();
    const num = parseFloat(text.replace(',', ''));
    if (isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  syncOneTimePurchasePrice() {
    const vid = this._getCurrentVariantId();
    if (vid) this.updateVisibleVariantPrices(vid);
  }

  // ------------------------------------------------------------------
  //  Color Sync
  // ------------------------------------------------------------------

  onColorChanged(newColorHandle) {
    if (!this.useColorMatch) return;

    // Swap active color sets
    this.blockEl.querySelectorAll('.sub-colour-set.colour').forEach(el => {
      el.classList.toggle('active', el.dataset.colourSwap === newColorHandle);
    });
    this.blockEl.querySelectorAll('.no-sub-colour-set.colour').forEach(el => {
      el.classList.toggle('active', el.dataset.colourSwap === newColorHandle);
    });

    // Derive preference: explicit global > current checked radio > default selection
    let preference = window.subscriptionPreference;
    if (!preference) {
      const currentlyChecked = this.getCheckedInput();
      if (currentlyChecked) {
        preference = currentlyChecked.classList.contains('no-subscription-input') ? 'no-subscription' : 'subscription';
      } else if (this.defaultSelection === 'none') {
        return;
      } else {
        preference = this.defaultSelection === 'no_subscription' ? 'no-subscription' : 'subscription';
      }
    }

    const currentChecked = this.getCheckedInput();
    let newInput = null;

    if (preference === 'no-subscription') {
      const noSubSet = this.blockEl.querySelector('.no-sub-colour-set[data-colour-swap="' + newColorHandle + '"]');
      newInput = noSubSet
        ? noSubSet.querySelector('input.no-subscription-input')
        : this.blockEl.querySelector('.no-subscription-input');
    } else {
      const subSet = this.blockEl.querySelector('.sub-colour-set[data-colour-swap="' + newColorHandle + '"]');
      if (subSet) {
        newInput = subSet.querySelector('input[name="subscription-plan"]');
      }
    }

    if (newInput && !newInput.disabled) {
      newInput.checked = true;
      if (currentChecked && currentChecked !== newInput) {
        currentChecked.checked = false;
      }
      this.updateMainProductSkip();
      this.updateSelectionState();
      newInput.dispatchEvent(new Event('change', { bubbles: true }));

      Promise.resolve().then(() => {
        const gp = window.PXUTheme?.jsGlobalProduct;
        if (gp) {
          const inputs = document.querySelectorAll('[data-product-selection]');
          if (gp.updatePricing) gp.updatePricing(inputs);
          if (gp.outOfStockHandler) gp.outOfStockHandler(false);
        }
      });
    }
  }

  syncSelectionToCurrentVariantColor() {
    if (!this.useColorMatch) return;
    const variantIdInput = document.querySelector('input[name="id"]');
    const selectedVariantInput = document.getElementById('selected-variant-id');
    const activeVariantId = (variantIdInput && variantIdInput.value) || (selectedVariantInput && selectedVariantInput.value);
    if (!activeVariantId) return;

    const newColorHandle = this.variantColorMap[String(activeVariantId)];
    if (!newColorHandle) return;

    this.onColorChanged(newColorHandle);
    this.syncAdditionalOfferColor(newColorHandle);
  }

  syncAdditionalOfferColor(colorHandle) {
    if (!this.hasAdditionalOffer) return;
    const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
    if (!addlInput) return;

    const mapScript = this.blockEl.querySelector('script[data-addl-colour-map-json]');
    if (!mapScript) return;

    let colourMap = [];
    try { colourMap = JSON.parse(mapScript.textContent); } catch(e) { return; }
    if (!colourMap.length) return;

    const targetHandle = (colorHandle || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    if (!targetHandle) return;

    const match = colourMap.find(item =>
      item.colour === targetHandle ||
      (item.colour_raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === targetHandle
    );
    if (!match) return;

    addlInput.value = String(match.id);
    addlInput.dataset.variantId = String(match.id);
    addlInput.dataset.price = String(match.price);
    addlInput.dataset.comparePrice = String(match.compare_price);
  }

  // ------------------------------------------------------------------
  //  Double Up (Bundle colour-match duplication)
  // ------------------------------------------------------------------

  initDoubleUp() {
    if (this.blockEl.dataset.doubleUpEnabled !== 'true') return;

    const strip = this.blockEl.querySelector('[data-double-up-strip]');
    if (!strip) return;

    const colourOptionGroups = document.querySelectorAll('.product_variant_option');
    let colourGroups = [];
    colourOptionGroups.forEach(group => {
      const optionIndex = group.dataset.optionIndex;
      const hasSwatches = group.querySelector('[data-swapper]') || group.querySelector('.swatch');
      const hasBundleSwatches = group.querySelector('.bundle-swatch-wrapper');
      const hasSwatchOptions = group.querySelector('.swatch__options') || group.querySelector('.colour');
      const titleRow = group.querySelector('.title__row span');
      const titleEl = titleRow || group.querySelector('.title');
      const titleText = titleEl ? titleEl.textContent.toLowerCase().trim() : '';
      const isColour = titleText.includes('colour') || titleText.includes('color') || titleText.includes('toothbrush') || hasSwatches || hasBundleSwatches || hasSwatchOptions;
      if (isColour) colourGroups.push({ group, optionIndex });
    });

    if (colourGroups.length < 2) return;

    strip.style.display = '';
    this.blockEl.setAttribute('data-double-up-visible', 'true');
    this._doubleUpReady = true;
    this._doubleUpStrip = strip;

    // Set initial expanded state based on whether subscription is already checked
    if (this.isSubscriptionChecked()) {
      strip.classList.add('is-expanded');
    }
    this._doubleUpActive = false;
    this._doubleUpSecondGroup = colourGroups[1];
    this._doubleUpInput = strip.querySelector('[data-double-up-input]');

    const toggleBtn = strip.querySelector('[data-double-up-toggle]');
    const activeState = strip.querySelector('[data-double-up-active]');
    const removeBtn = strip.querySelector('[data-double-up-remove]');

    if (toggleBtn) {
      const toggleHandler = () => this.activateDoubleUp();
      toggleBtn.addEventListener('click', toggleHandler);
      this._cleanup.push(() => toggleBtn.removeEventListener('click', toggleHandler));
    }

    if (removeBtn) {
      const removeHandler = () => this.deactivateDoubleUp();
      removeBtn.addEventListener('click', removeHandler);
      this._cleanup.push(() => removeBtn.removeEventListener('click', removeHandler));
    }

    const secondGroupInputHandler = () => {
      if (this._doubleUpActive) {
        setTimeout(() => this.syncDoubleUpVariant(), 50);
      }
    };
    this._doubleUpSecondGroup.group.addEventListener('change', secondGroupInputHandler);
    this._cleanup.push(() => this._doubleUpSecondGroup.group.removeEventListener('change', secondGroupInputHandler));
  }

  toggleDoubleUpVisibility() {
    if (!this._doubleUpReady || !this._doubleUpStrip) return;
    if (this.isSubscriptionChecked()) {
      this._doubleUpStrip.classList.add('is-expanded');
    } else {
      this._doubleUpStrip.classList.remove('is-expanded');
      if (this._doubleUpActive) this.deactivateDoubleUp();
    }
  }

  activateDoubleUp() {
    if (!this._doubleUpStrip || !this._doubleUpInput) return;
    this._doubleUpActive = true;

    const toggleBtn = this._doubleUpStrip.querySelector('[data-double-up-toggle]');
    const activeState = this._doubleUpStrip.querySelector('[data-double-up-active]');
    if (toggleBtn) toggleBtn.style.display = 'none';
    if (activeState) activeState.style.display = '';

    this.syncDoubleUpVariant();
    this._doubleUpInput.checked = true;
    this._doubleUpInput.dispatchEvent(new Event('change', { bubbles: true }));

    this.blockEl.dataset.doubleUpActive = 'true';

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  deactivateDoubleUp() {
    if (!this._doubleUpStrip || !this._doubleUpInput) return;
    this._doubleUpActive = false;

    const toggleBtn = this._doubleUpStrip.querySelector('[data-double-up-toggle]');
    const activeState = this._doubleUpStrip.querySelector('[data-double-up-active]');
    if (toggleBtn) toggleBtn.style.display = '';
    if (activeState) activeState.style.display = 'none';

    this._doubleUpInput.checked = false;
    this._doubleUpInput.dataset.price = '0';
    this._doubleUpInput.dataset.comparePrice = '0';
    this._doubleUpInput.dataset.variantId = '';
    this._doubleUpInput.value = '';

    this.blockEl.dataset.doubleUpActive = 'false';

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  syncDoubleUpVariant() {
    if (!this._doubleUpInput || !this._doubleUpSecondGroup) return;

    const checkedInput = this._doubleUpSecondGroup.group.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return;

    const colourHandle = checkedInput.dataset.swapper || checkedInput.dataset.optionValue || checkedInput.value;
    if (!colourHandle) return;

    const normalizedHandle = colourHandle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const mapScript = this.blockEl.querySelector('script[data-sub-colour-map-json]');
    if (!mapScript) return;

    let colourMap = [];
    try { colourMap = JSON.parse(mapScript.textContent); } catch(e) { return; }

    const match = colourMap.find(item =>
      item.colour === normalizedHandle ||
      (item.colour_raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === normalizedHandle
    );

    if (match) {
      this._doubleUpInput.value = String(match.id);
      this._doubleUpInput.dataset.variantId = String(match.id);
      this._doubleUpInput.dataset.price = String(match.price);
      this._doubleUpInput.dataset.comparePrice = String(match.compare_price);

      const sellingPlan = this.blockEl.dataset.firstSellingPlanId || '';
      this._doubleUpInput.dataset.sellingPlan = sellingPlan;

      const summaryEl = this._doubleUpStrip.querySelector('[data-double-up-summary]');
      if (summaryEl) {
        const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';
        const qty = Number(this._doubleUpInput.dataset.quantity || 1);
        const price = Shopify.formatMoney(match.price * qty, moneyFormat);
        const colourName = match.colour_raw || colourHandle;
        summaryEl.textContent = qty + 'x ' + colourName + ' — ' + price;
      }
    }

    if (this._doubleUpActive) {
      const gp = window.PXUTheme?.jsGlobalProduct;
      if (gp?.updatePricing) {
        gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
      }
    }
  }

  // ------------------------------------------------------------------
  //  Progressive Disclosure
  // ------------------------------------------------------------------

  checkProgressiveDisclosure() {
    if (this.blockEl.dataset.progressiveDisclosure !== 'true') return;

    const variantInput = document.getElementById('selected-variant-id');
    if (variantInput && variantInput.hasAttribute('data-options-unselected')) {
      this.blockEl.dataset.locked = 'true';
    } else {
      this.blockEl.dataset.locked = 'false';
    }
  }

  // ------------------------------------------------------------------
  //  Default Selection
  // ------------------------------------------------------------------

  applyDefaultSelection() {
    if (this.defaultSelection === 'none' && !this.hideNoSub) return;

    if (this.defaultSelection === 'subscription' || (this.hideNoSub && this.defaultSelection !== 'no_subscription')) {
      let subInput = null;
      if (this.useColorMatch) {
        const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
        if (activeSubSet) {
          subInput = activeSubSet.querySelector('input[name="subscription-plan"]');
        }
      } else {
        subInput = this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
      }
      if (this.isSellingPlanOnly) {
        subInput = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      }
      if (subInput && !subInput.disabled) {
        subInput.checked = true;
        subInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (this.defaultSelection === 'no_subscription' && !this.hideNoSub) {
      let noSubInput = null;
      if (this.useColorMatch && !this.isAddon) {
        const activeNoSubSet = this.blockEl.querySelector('.no-sub-colour-set.active');
        if (activeNoSubSet) {
          noSubInput = activeNoSubSet.querySelector('.no-subscription-input');
        }
      } else {
        noSubInput = this.blockEl.querySelector('.no-subscription-input');
      }
      if (this.isSellingPlanOnly) {
        noSubInput = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
      }
      if (noSubInput) {
        noSubInput.checked = true;
        noSubInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.updateSelectionState();
    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();
  }

  // ------------------------------------------------------------------
  //  Reset (after add-to-cart)
  // ------------------------------------------------------------------

  onProductSelectionsCleared() {
    setTimeout(() => {
      const allInputs = this.blockEl.querySelectorAll('input[type="radio"]');
      allInputs.forEach(input => { input.checked = false; });

      const fieldset = this.getFieldset();
      if (fieldset) {
        fieldset.removeAttribute('data-sticky-selection');
        fieldset.removeAttribute('data-selection');
        fieldset.setAttribute('data-sticky-selection', '');
        fieldset.classList.remove('not-loaded');
      }

      this.checkProgressiveDisclosure();

      const variantInput = document.getElementById('selected-variant-id');
      const shouldStayLocked = this.blockEl.dataset.progressiveDisclosure === 'true' &&
        variantInput && variantInput.hasAttribute('data-options-unselected');

      if (!shouldStayLocked && this.defaultSelection !== 'none') {
        setTimeout(() => this.applyDefaultSelection(), 20);
      }
    }, 100);
  }

  // ------------------------------------------------------------------
  //  Plan Dropdown
  // ------------------------------------------------------------------

  onDropdownChange(dropdown) {
    const selectedOpt = dropdown.options[dropdown.selectedIndex];
    if (!selectedOpt) return;

    const planIndex = parseInt(selectedOpt.value, 10);
    const sellingPlanId = selectedOpt.dataset.sellingPlanId || '';
    const variantId = selectedOpt.dataset.variantId || '';
    const quantity = selectedOpt.dataset.quantity || '1';

    this.blockEl.querySelectorAll('[data-plan-thumbnails] > div').forEach(row => {
      const rowIndex = parseInt(row.dataset.planIndex, 10);
      row.style.display = rowIndex === planIndex ? '' : 'none';
    });

    const deliveryInterval = selectedOpt.dataset.deliveryInterval || '';
    const visibleRow = this.blockEl.querySelector('[data-plan-thumbnails] > div[data-plan-index="' + planIndex + '"]:not([data-combo])');
    if (visibleRow) {
      const intervalEl = visibleRow.querySelector('.delivery-interval');
      if (intervalEl) {
        intervalEl.textContent = deliveryInterval;
        intervalEl.style.display = deliveryInterval ? '' : 'none';
      }
    }

    this.blockEl.querySelectorAll('[data-delivery-col-value]').forEach(el => {
      el.textContent = deliveryInterval;
    });

    if (deliveryInterval) {
      this.blockEl.dataset.mainDeliveryInterval = deliveryInterval;
    }

    this.blockEl.querySelectorAll('[data-plan-picker-option]').forEach(opt => {
      opt.classList.toggle('is-selected', opt.dataset.planIndex === String(planIndex));
    });

    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();

    if (this.isSellingPlanOnly) {
      const buyPlan = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      if (buyPlan) buyPlan.checked = true;
      this.updateSellingPlanOnATC();
      this.updateSelectionState();
      return;
    }

    const activeSubInput = this._getActiveSubInput();

    if (activeSubInput) {
      const comboData = this._getComboDropdownData();
      let newSellingPlan = sellingPlanId;
      let newVariant = variantId || (activeSubInput.value || '').split(',')[0];
      let newQty = quantity;

      if (comboData) {
        newSellingPlan += ',' + comboData.sellingPlanId;
        newVariant += ',' + comboData.variantId;
        newQty += ',' + comboData.quantity;
        activeSubInput.dataset.comboPrice = comboData.price;
        activeSubInput.dataset.comboCompare = comboData.comparePrice;
        activeSubInput.dataset.comboQty = comboData.quantity;
      } else {
        delete activeSubInput.dataset.comboPrice;
        delete activeSubInput.dataset.comboCompare;
        delete activeSubInput.dataset.comboQty;
      }

      activeSubInput.dataset.sellingPlan = newSellingPlan;
      activeSubInput.value = newVariant;
      activeSubInput.dataset.quantity = newQty;
      activeSubInput.checked = true;
      activeSubInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateSelectionState();
    this.updateAddItemBoxPrice();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  // ------------------------------------------------------------------
  //  Combo Plan Dropdown Change
  // ------------------------------------------------------------------

  onComboDropdownChange(comboDropdown) {
    const selectedOpt = comboDropdown.options[comboDropdown.selectedIndex];
    if (!selectedOpt) return;

    const comboIdx = parseInt(selectedOpt.value, 10);

    this.blockEl.querySelectorAll('[data-combo-thumbnails] .combo-row').forEach(row => {
      const rowIdx = parseInt(row.dataset.comboPlanIndex, 10);
      row.style.display = rowIdx === comboIdx ? '' : 'none';
    });

    this.blockEl.querySelectorAll('[data-combo-plan-picker-option]').forEach(opt => {
      opt.classList.toggle('is-selected', opt.dataset.comboPlanIndex === String(comboIdx));
    });

    const activeSubInput = this._getActiveSubInput();
    if (!activeSubInput) return;

    const mainSpId = (activeSubInput.dataset.sellingPlan || '').split(',')[0] || '';
    const mainVariant = (activeSubInput.value || '').split(',')[0] || '';
    const mainQty = (activeSubInput.dataset.quantity || '1').split(',')[0] || '1';

    const comboSpId = selectedOpt.dataset.sellingPlanId || '';
    const comboVariantId = selectedOpt.dataset.variantId || '';
    const comboQty = selectedOpt.dataset.quantity || '1';
    const comboPrice = selectedOpt.dataset.price || '0';
    const comboCompare = selectedOpt.dataset.comparePrice || comboPrice;

    activeSubInput.dataset.sellingPlan = mainSpId + ',' + comboSpId;
    activeSubInput.value = mainVariant + ',' + comboVariantId;
    activeSubInput.dataset.quantity = mainQty + ',' + comboQty;
    activeSubInput.dataset.comboPrice = comboPrice;
    activeSubInput.dataset.comboCompare = comboCompare;
    activeSubInput.dataset.comboQty = comboQty;

    const comboDelivery = selectedOpt.dataset.deliveryInterval || '';
    if (comboDelivery) {
      this.blockEl.dataset.comboDeliveryInterval = comboDelivery;
      this.blockEl.querySelectorAll('[data-combo-delivery-col-value]').forEach(el => {
        el.textContent = comboDelivery;
      });
    }

    this.updateAddItemBoxPrice();
    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  _getActiveSubInput() {
    if (this.isSellingPlanOnly) {
      return this.blockEl.querySelector('input[name="subscription-plan"]');
    }
    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      return activeSubSet ? activeSubSet.querySelector('input[name="subscription-plan"]') : null;
    }
    return this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
  }

  _getComboDropdownData() {
    const comboDropdown = this.blockEl.querySelector('[data-combo-plan-dropdown]');
    if (!comboDropdown) {
      if (this.blockEl.dataset.hasCombo !== 'true') return null;
      const activeSubInput = this._getActiveSubInput();
      if (!activeSubInput || !activeSubInput.dataset.comboPrice) return null;
      return {
        sellingPlanId: (activeSubInput.dataset.sellingPlan || '').split(',')[1] || '',
        variantId: (activeSubInput.value || '').split(',')[1] || '',
        quantity: (activeSubInput.dataset.quantity || '1').split(',')[1] || '1',
        price: activeSubInput.dataset.comboPrice || '0',
        comparePrice: activeSubInput.dataset.comboCompare || '0'
      };
    }
    const opt = comboDropdown.options[comboDropdown.selectedIndex];
    if (!opt) return null;
    return {
      sellingPlanId: opt.dataset.sellingPlanId || '',
      variantId: opt.dataset.variantId || '',
      quantity: opt.dataset.quantity || '1',
      price: opt.dataset.price || '0',
      comparePrice: opt.dataset.comparePrice || '0'
    };
  }

  // ------------------------------------------------------------------
  //  Variant → Plan Filtering (SPO mode)
  // ------------------------------------------------------------------

  _parseVariantPlanLinks(linksStr, universalStr) {
    const universalIndices = universalStr ? universalStr.split(',').map(s => parseInt(s, 10)) : [];
    const map = {};
    linksStr.split('|').forEach(entry => {
      const [vid, idx] = entry.split(':');
      if (!vid || idx === undefined) return;
      if (!map[vid]) map[vid] = [...universalIndices];
      map[vid].push(parseInt(idx, 10));
    });
    // Also store universal indices for variants that have no explicit links
    map._universal = universalIndices;
    return map;
  }

  _getCurrentVariantId() {
    // 1. Checked [data-product-selection] radio — universal across all picker types
    const checked = document.querySelector('[data-product-selection]:checked');
    if (checked && checked.value) return checked.value;
    // 2. Standard hidden inputs (variant-options.liquid sets these)
    const h = document.querySelector('input[name="id"]');
    if (h && h.value) return h.value;
    const s = document.getElementById('selected-variant-id');
    if (s && s.value) return s.value;
    // 3. Visible price inside buy-once label as last resort
    const buyOnceLabel = this.blockEl.querySelector('label[for="' + this.blockId + '-buy_once"]');
    if (buyOnceLabel) {
      const vp = buyOnceLabel.querySelector('.price[data-variant-id]:not([style*="display: none"])');
      if (vp && vp.dataset.variantId) return vp.dataset.variantId;
    }
    return '';
  }

  _findBuyPlanLabel() {
    const radio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    if (!radio) return null;
    // Label may be a parent (wrapped) or a sibling (for= attribute)
    return radio.closest('label') || this.blockEl.querySelector('label[for="' + radio.id + '"]');
  }

  onVariantChanged(variantId) {
    if (!this._variantPlanMap || !this.isSellingPlanOnly) return;

    const vid = String(variantId);
    if (vid === this._lastVariantId) return;
    this._lastVariantId = vid;

    const allowedIndices = this._variantPlanMap[vid] || this._variantPlanMap._universal || [];
    const hasPlansForVariant = allowedIndices.length > 0;
    const hadPlansBefore = this._lastHadPlans;
    this._lastHadPlans = hasPlansForVariant;

    // Always update price display for the active variant
    this.updateVisibleVariantPrices(vid);

    const dropdown = this.blockEl.querySelector('[data-plan-dropdown]');

    if (!hasPlansForVariant) {
      // Only toggle visibility when transitioning FROM plans → no plans
      if (hadPlansBefore !== false) {
        const buyPlanRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
        const buyPlanLabel = this._findBuyPlanLabel();
        const buyOnceRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
        if (buyPlanRadio) buyPlanRadio.style.display = 'none';
        if (buyPlanLabel) buyPlanLabel.style.display = 'none';
        this.blockEl.querySelectorAll('[data-plan-thumbnails], [data-plan-picker], [data-delivery-columns], [data-combo-delivery-columns], [data-combo-thumbnails], [data-combo-plan-picker]').forEach(el => el.style.display = 'none');
        if (buyOnceRadio) buyOnceRadio.checked = true;
      }
      this.updateSellingPlanOnATC();
      this.updateAtcText();
      this.updateSelectionState();
      return;
    }

    // Only toggle visibility when transitioning FROM no plans → has plans
    if (hadPlansBefore === false) {
      const buyPlanRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      const buyPlanLabel = this._findBuyPlanLabel();
      const buyOnceRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
      const buyOnceLabel = buyOnceRadio
        ? (buyOnceRadio.closest('label') || this.blockEl.querySelector('label[for="' + buyOnceRadio.id + '"]'))
        : null;
      if (buyPlanRadio) { buyPlanRadio.checked = true; buyPlanRadio.style.display = ''; }
      if (buyPlanLabel) buyPlanLabel.style.display = '';
      if (buyOnceRadio) buyOnceRadio.style.display = '';
      if (buyOnceLabel) buyOnceLabel.style.display = '';
      this.blockEl.querySelectorAll('[data-plan-thumbnails], [data-plan-picker], [data-delivery-columns], [data-combo-delivery-columns], [data-combo-thumbnails], [data-combo-plan-picker]').forEach(el => el.style.display = '');
    }

    // Update plan content (dropdown, thumbnails, picker) — runs for every variant change
    if (dropdown) {
      let firstVisibleIdx = -1;
      Array.from(dropdown.options).forEach(opt => {
        const planIdx = parseInt(opt.value, 10);
        const visible = allowedIndices.includes(planIdx);
        opt.hidden = !visible;
        opt.disabled = !visible;
        if (visible && firstVisibleIdx === -1) firstVisibleIdx = planIdx;
      });
      if (firstVisibleIdx >= 0) {
        const firstOpt = Array.from(dropdown.options).find(o => parseInt(o.value, 10) === firstVisibleIdx && !o.hidden);
        if (firstOpt) {
          dropdown.value = firstOpt.value;
          this.onDropdownChange(dropdown);
        }
      }
    }

    const planThumbnails = this.blockEl.querySelector('[data-plan-thumbnails]');
    if (planThumbnails) {
      const rows = planThumbnails.querySelectorAll('[data-plan-index]');
      let firstShown = false;
      rows.forEach(row => {
        const planIdx = parseInt(row.dataset.planIndex, 10);
        const visible = allowedIndices.includes(planIdx);
        row.style.display = visible && !firstShown ? '' : 'none';
        if (visible && !firstShown) firstShown = true;
      });
    }

    const planPickerPanel = this.blockEl.querySelector('[data-plan-picker-panel]');
    if (planPickerPanel) {
      const opts = planPickerPanel.querySelectorAll('[data-plan-picker-option]');
      let visibleCount = 0;
      let firstOpt = null;
      opts.forEach(opt => {
        const planIdx = parseInt(opt.dataset.planIndex, 10);
        const visible = allowedIndices.includes(planIdx);
        opt.style.display = visible ? '' : 'none';
        if (visible) {
          visibleCount++;
          if (!firstOpt) firstOpt = opt;
        }
        opt.classList.remove('is-selected');
      });
      if (firstOpt) firstOpt.classList.add('is-selected');
    }

    const visiblePlanCount = allowedIndices.length;
    const planPicker = this.blockEl.querySelector('[data-plan-picker]');
    if (planPicker) planPicker.style.display = '';
    if (planPickerPanel) {
      if (visiblePlanCount <= 1) {
        planPickerPanel.style.display = 'none';
        planPickerPanel.classList.remove('is-open');
      } else {
        planPickerPanel.style.display = '';
      }
    }
    const chevron = (planPicker || planThumbnails)?.querySelector('.plan-picker__chevron');
    if (chevron) chevron.style.display = visiblePlanCount > 1 ? '' : 'none';
    if (planThumbnails) {
      planThumbnails.classList.toggle('plan-picker__trigger', visiblePlanCount > 1);
    }

    this.updateSellingPlanOnATC();
    this.updateAtcText();
    this.updateSelectionState();
    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();
  }

  // ------------------------------------------------------------------
  //  Custom Plan Picker — thumbnails area triggers slide-down panel
  // ------------------------------------------------------------------

  bindPlanPicker() {
    this.blockEl.querySelectorAll('[data-plan-picker]:not([data-combo-plan-picker])').forEach(picker => {
      const panel = picker.querySelector('[data-plan-picker-panel]');
      const trigger = picker.querySelector('[data-plan-picker-trigger]');
      if (!panel || !trigger) return;

      trigger.addEventListener('click', (e) => {
        if (this.blockEl.dataset.locked === 'true') return;

        e.preventDefault();
        e.stopPropagation();

        const parentLabel = trigger.closest('label');
        if (parentLabel) {
          const forId = parentLabel.getAttribute('for');
          const radio = forId ? document.getElementById(forId) : parentLabel.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const isOpen = picker.classList.contains('is-open');
        this._closeAllPlanPickers();
        if (!isOpen) this._openPlanPanel(picker, panel);
      });

      panel.querySelectorAll('[data-plan-picker-option]').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const planIndex = option.dataset.planIndex;

          panel.querySelectorAll('[data-plan-picker-option]').forEach(o => o.classList.remove('is-selected'));
          option.classList.add('is-selected');

          const nativeSelect = this.blockEl.querySelector('[data-plan-dropdown]');
          if (nativeSelect) {
            nativeSelect.value = planIndex;
            this.onDropdownChange(nativeSelect);
          }

          this._closePlanPanel(picker, panel);
        });
      });
    });

    this.bindComboPlanPicker();
    document.addEventListener('click', () => this._closeAllPlanPickers());
  }

  bindComboPlanPicker() {
    this.blockEl.querySelectorAll('[data-combo-plan-picker]').forEach(picker => {
      const panel = picker.querySelector('[data-combo-plan-picker-panel]');
      const trigger = picker.querySelector('[data-combo-plan-picker-trigger]');
      if (!panel || !trigger) return;

      trigger.addEventListener('click', (e) => {
        if (this.blockEl.dataset.locked === 'true') return;
        e.preventDefault();
        e.stopPropagation();

        const isOpen = picker.classList.contains('is-open');
        this._closeAllPlanPickers();
        if (!isOpen) this._openPlanPanel(picker, panel);
      });

      panel.querySelectorAll('[data-combo-plan-picker-option]').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const comboIdx = option.dataset.comboPlanIndex;

          panel.querySelectorAll('[data-combo-plan-picker-option]').forEach(o => o.classList.remove('is-selected'));
          option.classList.add('is-selected');

          const comboSelect = this.blockEl.querySelector('[data-combo-plan-dropdown]');
          if (comboSelect) {
            comboSelect.value = comboIdx;
            this.onComboDropdownChange(comboSelect);
          }

          this._closePlanPanel(picker, panel);
        });
      });
    });

    const comboDD = this.blockEl.querySelector('[data-combo-plan-dropdown]');
    if (comboDD) {
      comboDD.addEventListener('change', () => this.onComboDropdownChange(comboDD));
    }
  }

  _openPlanPanel(picker, panel) {
    clearTimeout(picker._closeTimer);
    picker.classList.add('is-open');
    panel.style.maxHeight = panel.scrollHeight + 'px';
  }

  _closePlanPanel(picker, panel) {
    panel.style.maxHeight = '0';
    clearTimeout(picker._closeTimer);
    picker._closeTimer = setTimeout(() => picker.classList.remove('is-open'), 250);
  }

  _closeAllPlanPickers() {
    this.blockEl.querySelectorAll('[data-plan-picker]:not([data-combo-plan-picker])').forEach(picker => {
      const panel = picker.querySelector('[data-plan-picker-panel]');
      if (panel) this._closePlanPanel(picker, panel);
    });
    this.blockEl.querySelectorAll('[data-combo-plan-picker]').forEach(picker => {
      const panel = picker.querySelector('[data-combo-plan-picker-panel]');
      if (panel) this._closePlanPanel(picker, panel);
    });
  }

  // ------------------------------------------------------------------
  //  OTP Conversion Strip (expand when buy-once selected)
  // ------------------------------------------------------------------

  bindOtpConversionStrip() {
    const strips = this.blockEl.querySelectorAll('[data-otp-conversion-strip]');
    if (!strips.length) return;

    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';

    this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
      input.addEventListener('change', () => this._syncOtpStrips(), true);
    });

    strips.forEach(strip => {
      const btn = strip.querySelector('[data-otp-switch-to-sub]');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._switchToSubscription();
      });
    });
  }

  _syncOtpStrips() {
    const isBuyOnce = !this.isSubscriptionChecked();
    this.blockEl.querySelectorAll('[data-otp-conversion-strip]').forEach(strip => {
      const label = strip.closest('label');
      if (!label) return;
      const input = label.previousElementSibling;
      const isThisChecked = input && input.checked;

      if (isBuyOnce && isThisChecked) {
        this._openOtpStrip(strip);
      } else {
        this._closeOtpStrip(strip);
      }
    });
  }

  _openOtpStrip(strip) {
    if (strip.classList.contains('is-expanded')) return;

    strip.style.transition = 'none';
    strip.classList.add('is-expanded');
    void strip.offsetHeight;
    const targetHeight = strip.scrollHeight;

    strip.style.opacity = '0';
    strip.style.padding = '0';
    strip.style.marginTop = '0';
    strip.style.maxHeight = '0';
    void strip.offsetHeight;

    strip.style.transition = '';
    void strip.offsetHeight;

    strip.style.maxHeight = targetHeight + 'px';
    strip.style.removeProperty('opacity');
    strip.style.removeProperty('padding');
    strip.style.removeProperty('margin-top');
  }

  _closeOtpStrip(strip) {
    if (!strip.classList.contains('is-expanded')) return;

    strip.style.maxHeight = strip.scrollHeight + 'px';
    strip.style.opacity = '1';
    strip.style.padding = '9px 12px 9px 10px';
    strip.style.marginTop = '4px';
    void strip.offsetHeight;

    strip.style.maxHeight = '0';
    strip.style.opacity = '0';
    strip.style.padding = '0';
    strip.style.marginTop = '0';

    strip.addEventListener('transitionend', function handler(e) {
      if (e.propertyName !== 'max-height') return;
      strip.removeEventListener('transitionend', handler);
      strip.classList.remove('is-expanded');
      strip.style.removeProperty('max-height');
      strip.style.removeProperty('opacity');
      strip.style.removeProperty('padding');
      strip.style.removeProperty('margin-top');
    });
  }

  _switchToSubscription() {
    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';
    let subInput = null;

    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      if (activeSubSet) subInput = activeSubSet.querySelector('input[name="' + radioName + '"]');
    }
    if (!subInput && this.isSellingPlanOnly) {
      subInput = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    }
    if (!subInput) {
      subInput = this.blockEl.querySelector('input[name="' + radioName + '"]:not(.no-subscription-input):not([id$="-buy_once"])');
    }

    if (subInput) {
      subInput.checked = true;
      subInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ------------------------------------------------------------------
  //  Event Binding
  // ------------------------------------------------------------------

  bindEvents() {
    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';

    // Radio change — track preference and update state
    this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
      const handler = (event) => {
        if (event && event.isTrusted) {
          const isSub = !input.classList.contains('no-subscription-input') &&
                        input.id !== (this.blockId + '-buy_once');
          window.subscriptionPreference = isSub ? 'subscription' : 'no-subscription';
        }

        if (this.isSellingPlanOnly) {
          this.updateSellingPlanOnATC();
        }

        this.updateMainProductSkip();
        this.updateSelectionState();
        this.syncCompactPriceInPlanRow();
        this.updatePricePerDay();
        this.toggleDoubleUpVisibility();

        const gp = window.PXUTheme?.jsGlobalProduct;
        if (gp?.updatePricing) {
          gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
        }
      };
      input.addEventListener('change', handler, true);
    });

    // When additional offer exists, make primary subscription radios deselectable (behave like checkboxes)
    if (this.hasAdditionalOffer) {
      this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
        if (input.classList.contains('no-subscription-input')) return;
        let lastCheckedState = input.checked;
        const clickToggleHandler = (e) => {
          if (lastCheckedState && input.checked) {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          lastCheckedState = input.checked;
        };
        input.addEventListener('click', clickToggleHandler);
        this._cleanup.push(() => input.removeEventListener('click', clickToggleHandler));

        const changeTracker = () => { lastCheckedState = input.checked; };
        input.addEventListener('change', changeTracker);
        this._cleanup.push(() => input.removeEventListener('change', changeTracker));
      });
    }

    // Additional subscription checkbox (dual-offer mode)
    if (this.hasAdditionalOffer) {
      const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
      if (addlInput) {
        const addlHandler = (event) => {
          if (addlInput.checked) {
            const noSubInput = this.blockEl.querySelector('.no-subscription-input');
            if (noSubInput && noSubInput.checked) {
              noSubInput.checked = false;
            }
          }
          this.updateMainProductSkip();
          this.updateSelectionState();
          const gp = window.PXUTheme?.jsGlobalProduct;
          if (gp?.updatePricing) {
            gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
          }
        };
        addlInput.addEventListener('change', addlHandler);
        this._cleanup.push(() => addlInput.removeEventListener('change', addlHandler));
      }

      // Additional offer dropdown
      this.blockEl.querySelectorAll('[data-addl-plan-dropdown]').forEach(dd => {
        const ddHandler = () => {
          const selectedOpt = dd.options[dd.selectedIndex];
          if (!selectedOpt || !addlInput) return;
          const spId = selectedOpt.dataset.sellingPlanId || '';
          const qty = selectedOpt.dataset.quantity || '1';
          const variantId = selectedOpt.dataset.variantId || '';
          addlInput.dataset.sellingPlan = spId;
          addlInput.dataset.quantity = qty;
          if (variantId) {
            addlInput.value = variantId;
            addlInput.dataset.variantId = variantId;
          }
          addlInput.checked = true;
          addlInput.dispatchEvent(new Event('change', { bubbles: true }));
        };
        dd.addEventListener('change', ddHandler);
        this._cleanup.push(() => dd.removeEventListener('change', ddHandler));
      });

      // Override no-subscription click to also deselect additional offer
      const noSubInputs = this.blockEl.querySelectorAll('.no-subscription-input');
      noSubInputs.forEach(noSubInput => {
        const noSubAddlHandler = () => {
          if (noSubInput.checked && addlInput) {
            addlInput.checked = false;
          }
        };
        noSubInput.addEventListener('change', noSubAddlHandler);
        this._cleanup.push(() => noSubInput.removeEventListener('change', noSubAddlHandler));
      });
    }

    // Plan dropdown change (native select kept as data source)
    this.blockEl.querySelectorAll('[data-plan-dropdown]').forEach(dd => {
      dd.addEventListener('change', () => this.onDropdownChange(dd));
    });

    // Custom plan picker interactions
    this.bindPlanPicker();

    // OTP conversion message strip (expand/collapse + switch button)
    this.bindOtpConversionStrip();

    // Color sync via variantColorSelected
    const colorHandler = (event) => {
      const newColorHandle = event.detail && event.detail.variant;
      if (newColorHandle) {
        this.onColorChanged(newColorHandle);
        this.syncAdditionalOfferColor(newColorHandle);
      }
      setTimeout(() => this.syncOneTimePurchasePrice(), 50);
    };
    document.addEventListener('variantColorSelected', colorHandler, true);
    this._cleanup.push(() => document.removeEventListener('variantColorSelected', colorHandler, true));

    // Reset after add to cart
    const clearHandler = () => this.onProductSelectionsCleared();
    document.addEventListener('productSelectionsCleared', clearHandler);
    this._cleanup.push(() => document.removeEventListener('productSelectionsCleared', clearHandler));

    // Variant change — sync prices, color, and plan filtering
    const changeHandler = (e) => {
      if (e.target.matches && e.target.matches('[data-product-selection]')) {
        if (this._variantPlanMap && e.target.value) {
          this.onVariantChanged(e.target.value);
        }
        setTimeout(() => this.syncOneTimePurchasePrice(), 10);
      }
      if (e.target.matches && e.target.matches('input[name="id"]')) {
        if (this._variantPlanMap) {
          this.onVariantChanged(e.target.value);
        }
        setTimeout(() => this.syncOneTimePurchasePrice(), 10);
        setTimeout(() => this.syncSelectionToCurrentVariantColor(), 0);
      }
      if (e.target.id === 'selected-variant-id' && this._variantPlanMap) {
        this.onVariantChanged(e.target.value);
      }
    };
    document.addEventListener('change', changeHandler);
    this._cleanup.push(() => document.removeEventListener('change', changeHandler));

    // Variant→plan filtering: multiple strategies to detect variant changes
    if (this._variantPlanMap) {
      let lastVid = '';

      const getVariantId = () => this._getCurrentVariantId();

      const fireVariantChange = (source) => {
        const newVid = getVariantId();
        if (newVid && newVid !== lastVid) {
          lastVid = newVid;
          this.onVariantChanged(lastVid);
        }
      };

      // Strategy 1: document-level click (variant buttons are outside this block)
      const clickHandler = () => setTimeout(() => fireVariantChange('click'), 80);
      document.addEventListener('click', clickHandler);
      this._cleanup.push(() => document.removeEventListener('click', clickHandler));

      // Strategy 2: poll for value changes every 200ms
      const pollInterval = setInterval(() => fireVariantChange('poll'), 200);
      this._cleanup.push(() => clearInterval(pollInterval));

      // Strategy 3: observe price-div visibility within our block as a
      //             secondary signal — if the theme shows a different
      //             price-<vid> div, derive the active variant from it
      const buyOnceLabel = this.blockEl.querySelector('label[for="' + this.blockId + '-buy_once"]');
      if (buyOnceLabel) {
        const priceObserver = new MutationObserver(() => {
          const visiblePrice = buyOnceLabel.querySelector('.price[data-variant-id]:not([style*="display: none"])');
          if (visiblePrice) {
            const vid = visiblePrice.dataset.variantId;
            if (vid && vid !== lastVid) {
              lastVid = vid;
              this.onVariantChanged(lastVid);
            }
          }
        });
        priceObserver.observe(buyOnceLabel, { subtree: true, attributes: true, attributeFilter: ['style'], childList: true });
        this._cleanup.push(() => priceObserver.disconnect());
      }
    }

    // Progressive disclosure — watch variant input
    if (this.blockEl.dataset.progressiveDisclosure === 'true') {
      const variantInput = document.getElementById('selected-variant-id');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          const wasLocked = this.blockEl.dataset.locked === 'true';
          this.checkProgressiveDisclosure();
          const isNowUnlocked = this.blockEl.dataset.locked === 'false';
          if (wasLocked && isNowUnlocked && this.defaultSelection !== 'none' && !this.getCheckedInput()) {
            this.applyDefaultSelection();
          }
        });
        observer.observe(variantInput, { attributes: true, attributeFilter: ['data-options-unselected'] });
        this._cleanup.push(() => observer.disconnect());
      }
    }
  }

  // ------------------------------------------------------------------
  //  Initialization
  // ------------------------------------------------------------------

  init() {
    if (!window.subscriptionPreference) {
      window.subscriptionPreference = null;
    }

    this.bindEvents();
    this.checkProgressiveDisclosure();

    // Remove loading state
    const fieldset = this.getFieldset();
    if (fieldset) fieldset.classList.remove('not-loaded');

    // Apply default selection
    this.applyDefaultSelection();

    // Variant→plan filtering: apply initial filter based on current variant
    if (this._variantPlanMap) {
      const initVid = this._getCurrentVariantId();
      if (initVid && String(initVid) !== this._lastVariantId) {
        // Variant differs from Liquid SSR — re-run full filtering
        this.onVariantChanged(initVid);
      } else if (initVid) {
        // Liquid already rendered the correct state for this variant;
        // just sync the dropdown selection and selling plan on ATC.
        const dropdown = this.blockEl.querySelector('[data-plan-dropdown]');
        if (dropdown) {
          const allowedIndices = this._variantPlanMap[String(initVid)]
            || this._variantPlanMap._universal || [];
          const firstOpt = Array.from(dropdown.options).find(
            o => allowedIndices.includes(parseInt(o.value, 10)) && !o.hidden
          );
          if (firstOpt && dropdown.value !== firstOpt.value) {
            dropdown.value = firstOpt.value;
          }
          this.onDropdownChange(dropdown);
        }
      }
    }

    if (this.isSellingPlanOnly) {
      this.updateSellingPlanOnATC();
    }

    this.updateMainProductSkip();
    this.updateSelectionState();
    this.syncOneTimePurchasePrice();
    this.syncCompactPriceInPlanRow();
    this.updatePricePerDay();
    this.syncSelectionToCurrentVariantColor();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }

    setTimeout(() => this.updateAtcText(), 50);

    // Sync OTP conversion strips with initial selection state
    setTimeout(() => this._syncOtpStrips(), 100);

    // Double Up feature (bundle colour match)
    this.initDoubleUp();
  }

  // ------------------------------------------------------------------
  //  Cleanup (for reloadProductForm)
  // ------------------------------------------------------------------

  destroy() {
    this._cleanup.forEach(fn => fn());
    this._cleanup = [];
  }
}

window.PXUTheme.jsGlobalProduct = {
  buttonLabels: {},
  _initialFormHTML: null,
  _subscriptionControllers: [],

  // ------------------------------------------------------------------
  //  Subscription Block Orchestrator
  //  Picks up configs registered by inline Liquid scripts and creates
  //  a SubscriptionBlockController for each block on the page.
  // ------------------------------------------------------------------
  initSubscriptionBlocks() {
    this._subscriptionControllers.forEach(c => c.destroy());
    this._subscriptionControllers = [];

    const queue = window._subscriptionBlockQueue || [];
    queue.forEach(entry => {
      if (!entry.blockEl) return;
      const controller = new SubscriptionBlockController(entry.blockEl, entry);
      controller.init();
      this._subscriptionControllers.push(controller);
    });
    window._subscriptionBlockQueue = [];
  },

  async init() {
    // Capture pristine HTML before any handlers modify the DOM
    if (!this._initialFormHTML) {
      this.captureInitialFormState();
    }
    
    this.initButtonLabels();
    this.watchSwappers(); // Watch for changes in variant
    if (document.querySelector("[data-product-selection]")) {
      this.watchInputs();
    }
    if (document.querySelector("[data-product-atc]")) {
      this.watchATCs();
    }
    if (document.querySelector(".global-product__atc-bar")) {
      this.watchATCScroll();
    }
    if (document.querySelectorAll("[data-sticky-selection]") && document.getElementById("getStartedBtn")) {
      this.updateFloatingATC();
    }
    this.updateKlarnaPrice();

    if (document.querySelectorAll("input[data-update-selected-variant]")) {
      this.watchSelectedVariantChanges();
    }

    if (document.body.getAttribute("data-intelligems-enabled") === "true") {
      this.initIntelligems();
    }

    this.outOfStockHandler(false);

    this.onLoadElements();

    // Ensure Back in Stock widget is positioned inside the last product options block
    this.positionBackInStockWidget();

    // Initialize progressive disclosure for all option blocks
    this.initProgressiveDisclosure();

    // Initialize subscription block controllers from inline Liquid configs
    this.initSubscriptionBlocks();

    // Watch for text changes on main ATC button and sync to sticky button
    this.watchMainATCTextChanges();
    
    // Watch for out of stock text changes and disable buttons
    this.watchOutOfStockTextChanges();
  },

  initButtonLabels() {
    const atcBtn = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedTextEl = getStartedBtn ? getStartedBtn.querySelector(".text") : null;
    const getStartedInitialText = getStartedTextEl ? getStartedTextEl.textContent.trim() : "";

    this.buttonLabels = {
      addToCart: (atcBtn?.dataset.labelAddToCart || (atcBtn?.textContent || "")).trim(),
      outOfStock: atcBtn?.dataset.oosLabel || "",
      selectOptions: atcBtn?.dataset.labelSelectOptions || "",
      preorder: atcBtn?.dataset.labelPreorder || "",
      floatingAddToCart: getStartedBtn?.dataset.labelAddToCart || atcBtn?.dataset.labelAddToCart || (atcBtn?.textContent || "").trim(),
      floatingOutOfStock: getStartedBtn?.dataset.labelOutOfStock || atcBtn?.dataset.oosLabel || "",
      floatingSelectOptions: getStartedBtn?.dataset.labelSelectOptions || atcBtn?.dataset.labelSelectOptions || "",
      floatingDefault: getStartedBtn?.dataset.labelDefault || getStartedInitialText
    };
  },

  getButtonLabel(key, fallback = "") {
    if (!this.buttonLabels) {
      return fallback;
    }
    return this.buttonLabels[key] || fallback;
  },

  shouldPreserveButtonText(button, event) {
    if (!button || !button.classList.contains("restock-rocket-preorder-button")) {
      return false;
    }

    if (event && event.target && event.target.matches("[data-product-selection]")) {
      const target = event.target;
      const isSubscriptionOption =
        target.name === "subscription-plan" ||
        target.name === "selling_plan_option" ||
        target.closest(".global-product__global-subscription") ||
        target.closest(".global-product__options__subscription") ||
        target.hasAttribute("data-subscription-option");

      if (!isSubscriptionOption) {
        return false;
      }
    }

    return true;
  },

  getActiveSubscriptionLabel() {
    const block = document.querySelector('.subscription-options-block[data-subscribe-atc-text]');
    if (!block) return null;
    const label = block.dataset.subscribeAtcText;
    if (!label) return null;
    const isSPO = block.dataset.sellingPlanOnly === 'true';
    const radioName = isSPO ? 'selling_plan_option' : 'subscription-plan';
    const checked = block.querySelector('input[name="' + radioName + '"]:checked');
    if (!checked) return null;
    if (checked.classList.contains('no-subscription-input')) return null;
    if (isSPO && checked.id && checked.id.endsWith('-buy_once')) return null;
    return label;
  },

  updateKlarnaPrice() {
    const klarnaEl = document.querySelector("klarna-placement");
    const priceEl = document.querySelector("[data-klarna-price]");

    if (klarnaEl && priceEl) {
      const price = Number(priceEl.dataset.klarnaPrice);

      if (price < 5000) {
        klarnaEl.classList.add("hidden");
      } else {
        klarnaEl.classList.remove("hidden");
      }

      klarnaEl.setAttribute("data-purchase-amount", price);

      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({ eventName: "refresh-placements" });

      klarnaEl.classList.add("Reveal");
    }
  },

  watchSelectedVariantChanges() {
    const selectedVariantInputs = document.querySelectorAll("input[data-update-selected-variant]");

    if (selectedVariantInputs.length === 0) {
      console.warn("No selected variant inputs found.");
      return;
    }

    selectedVariantInputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        const newVariant = event.target.value;
        document.querySelector("input[name='id']").value = newVariant;
      });
    });
  },

  watchDisabledBlocks() {
    const products = document.querySelectorAll("product");
    let enableDisabled = true;

    for (const prod of products) {
      if (prod.dataset.skipForSubscription === "true") continue;
      if (!prod.querySelector("[data-product-selection]:checked")) {
        enableDisabled = false;
      }
    }

    if (enableDisabled) {
      const disabledBlocks = document.querySelectorAll("[data-product-disabled]");
      for (const block of disabledBlocks) {
        block.classList.add("enabled");
      }
    }
  },

  disableDisabledBlocks() {
    const disabledBlocks = document.querySelectorAll("[data-product-disabled]");
    for (const block of disabledBlocks) {
      block.classList.remove("enabled");
    }
  },

  // Watch inputs to show subscription options
  watchInputs() {
    if (this._subscriptionActivated === undefined) {
      this._subscriptionActivated = false;
    }
    
    // Remove previous document-level change handler to prevent stacking
    if (this._watchInputsChangeHandler) {
      document.removeEventListener("change", this._watchInputsChangeHandler);
    }
    
    const updateHandler = () => {
      const inputs = document.querySelectorAll("[data-product-selection]");
      const subscriptionBlock = document.querySelector(".global-product__options__subscription");
      
      if (subscriptionBlock) {
        subscriptionBlock.classList.add("active");
      }
      this.watchDisabledBlocks();
      this.updatePricing(inputs);
    };

    const togglePaymentButtons = () => {
      const paymentButtons = document.querySelectorAll('.shopify-payment-button, .additional_payment_container');
      
      // Check all subscription input types (unified block uses subscription-plan / selling_plan_option)
      const checkedInputs = document.querySelectorAll(
        'input[name="subscription-plan"]:checked, input[name="selling_plan_option"]:checked'
      );
      
      let hasSubscription = false;
      checkedInputs.forEach(input => {
        if (input.dataset.sellingPlan) {
          hasSubscription = true;
        }
      });
      
      paymentButtons.forEach(button => {
        if (hasSubscription) {
          button.style.display = 'none';
        } else {
          button.style.display = '';
        }
      });
    };

    this._watchInputsChangeHandler = (event) => {
      if (event.target.matches("[data-product-selection]")) {
        updateHandler();
      }
      
      // Toggle payment buttons when subscription options change
      if (event.target.matches('input[name="subscription-plan"], input[name="selling_plan_option"]')) {
        togglePaymentButtons();
      }
      
      const colorOption = event.target.closest('[data-color-option]');
      if (colorOption && event.target.matches('input[type="radio"]')) {
        const input = event.target;
        const colorValue = input.dataset.colour || input.value;
        const label = input.closest('label');
        const title = label?.dataset?.title || '';
        document.dispatchEvent(new CustomEvent('variantColorSelected', {
          detail: {
            variant: colorValue,
            color: colorValue,
            title: title,
            source: 'colorOption',
            input: input
          }
        }));
      }
      
      this.outOfStockHandler(event);
      setTimeout(() => this.outOfStockHandler(false), 0);
    };

    document.addEventListener("change", this._watchInputsChangeHandler);
    
    togglePaymentButtons();
  },

  outOfStockHandler(event) {
    const btn = document.querySelector("[data-product-atc]");

    if (event) {
      if (btn) {
        const oosLabel = this.getButtonLabel("outOfStock", btn.dataset.oosLabel || "");
        const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
        if (event.target.matches("[data-out-of-stock='true']")) {
          btn.classList.add("disabled");
          btn.setAttribute("aria-disabled", "true");
          if (!this.shouldPreserveButtonText(btn, event)) {
            btn.textContent = oosLabel || addToCartLabel || btn.textContent;
          }
        } else {
          btn.classList.remove("disabled");
          btn.setAttribute("aria-disabled", "false");
          if (!this.shouldPreserveButtonText(btn, event)) {
            const subLabel = this.getActiveSubscriptionLabel();
            btn.textContent = subLabel || addToCartLabel || btn.textContent;
          }
        }
      }

      const additionalPaymentContainer = document.querySelector(".global-product .additional_payment_container");
      if (additionalPaymentContainer) {
        if (event.target.matches("[data-out-of-stock='true']")) {
          additionalPaymentContainer.classList.add("hidden");
        } else {
          additionalPaymentContainer.classList.remove("hidden");
        }
      }

      return;
    } else {
      const inputs = document.querySelectorAll("[data-product-selection]");
      if (!inputs.length) return;

      let allOutOfStock = true;
      let checkedOutOfStock = false;
      let hasOptionsUnselected = false;

      for (const input of inputs) {
        const isOutOfStock = input.dataset.outOfStock === "true";
        const isOptionsUnselected = input.hasAttribute("data-options-unselected");

        // Only consider out-of-stock status if options are selected
        if (!isOptionsUnselected) {
          if (!isOutOfStock) {
            allOutOfStock = false;
          }

          if (input.checked && isOutOfStock) {
            checkedOutOfStock = true;
          }
        }

        if (input.checked && isOptionsUnselected) {
          hasOptionsUnselected = true;
        }
      }

      // Disable button if: options unselected OR variant is out of stock (when options are selected)
      const disableButton = hasOptionsUnselected || checkedOutOfStock || (!hasOptionsUnselected && allOutOfStock);

      const additionalPaymentContainer = document.querySelector(".global-product .additional_payment_container");

      if (disableButton) {
        if (btn) {
          btn.classList.add("disabled");
          btn.setAttribute("aria-disabled", "true");
          const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
          const oosLabel = this.getButtonLabel("outOfStock", btn.dataset.oosLabel || "");
          const subLabel = this.getActiveSubscriptionLabel();
          const preserveText = this.shouldPreserveButtonText(btn);
          if (hasOptionsUnselected) {
            if (!preserveText) {
              btn.textContent = subLabel || addToCartLabel || btn.textContent;
            }
            btn.setAttribute("data-options-unselected", "");
          } else if (checkedOutOfStock) {
            if (!preserveText) {
              btn.textContent = oosLabel || addToCartLabel || btn.textContent;
            }
            btn.removeAttribute("data-options-unselected");
          } else {
            if (!preserveText) {
              btn.textContent = subLabel || addToCartLabel || btn.textContent;
            }
            btn.removeAttribute("data-options-unselected");
          }
        }
        if (additionalPaymentContainer && !hasOptionsUnselected) {
          additionalPaymentContainer.classList.add('hidden');
        }
        // Auto-show BIS under options if present (with slide-in)
        // Only show if out of stock AND all options are selected (not when options are unselected)
        const bis = document.getElementById('backInStockWidget');
        if (bis) {
          if (!hasOptionsUnselected && checkedOutOfStock) {
            // Show back in stock widget only when all options selected AND variant is out of stock
            const hiddenId = document.querySelector("input[name='id']");
            if (hiddenId && hiddenId.value) bis.setAttribute('data-variant-id', hiddenId.value);
            bis.classList.remove('is-visible');
            bis.style.display = '';
            requestAnimationFrame(() => bis.classList.add('is-visible'));
          } else {
            // Hide back in stock widget when options are unselected or variant is available
            bis.classList.remove('is-visible');
            setTimeout(() => { bis.style.display = 'none'; }, 320);
          }
        }
      } else {
        if (btn) {
          btn.classList.remove("disabled");
          btn.setAttribute("aria-disabled", "false");
          if (!this.shouldPreserveButtonText(btn)) {
            const subLabel = this.getActiveSubscriptionLabel();
            const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
            btn.textContent = subLabel || addToCartLabel || btn.textContent;
          }
          btn.removeAttribute("data-options-unselected");
        }
        if (additionalPaymentContainer) additionalPaymentContainer.classList.remove('hidden');

        // Hide Back in Stock widget when available (slide-out)
        const bis = document.getElementById('backInStockWidget');
        if (bis) {
          bis.classList.remove('is-visible');
          setTimeout(() => { bis.style.display = 'none'; }, 320);
        }
      }
      
      // Keep sticky ATC in sync
      if (this.updateFloatingATC) this.updateFloatingATC();
    }
  },

  updatePricing(inputs) {
    let price = 0;
    let comparePrice = 0;
    let sellingPlan = false;
    let sellingPlanMainOnly = 0;
    let sellingPlanComboOnly = 0;
    let sellingPlanLines = [];
    // Track variant-only price (excludes subscription inputs) for price/reviews display
    let variantOnlyPrice = 0;
    let variantOnlyComparePrice = 0;
    // Track selected main variant pricing for savings-breakdown visibility rules
    let selectedVariantPrice = 0;
    let selectedVariantComparePrice = 0;

    for (const input of inputs) {
      // Skip inputs inside a product element marked for subscription replacement
      const parentProduct = input.closest("product");
      if (parentProduct && parentProduct.dataset.skipForSubscription === "true") continue;

      if (input.checked == true) {
        const isSubscriptionInput = input.dataset.sellingPlan || 
          input.closest('.global-product__global-subscription') ||
          input.closest('.global-product__options__subscription') || 
          input.name === 'subscription-plan' ||
          input.name === 'selling_plan_option';
        const isMainVariantInput = !isSubscriptionInput;

        const qtyStr = input.dataset.quantity || '0';
        const inputQty = Number(qtyStr.split(',')[0]) || 0;
        const inputPrice = Number(input.dataset.price || 0);
        const inputCompare = Number(input.dataset.comparePrice || 0);

        let comboAddon = 0;
        let comboCompareAddon = 0;
        const cPrice = Number(input.dataset.comboPrice || 0);
        const cCompare = Number(input.dataset.comboCompare || cPrice);
        const cQty = Number(input.dataset.comboQty || 0);
        if (cPrice > 0 && cQty > 0) {
          comboAddon = cPrice * cQty;
          comboCompareAddon = cCompare * cQty;
        }

        if (inputQty > 0) {
          price = Number(price) + inputPrice * inputQty + comboAddon;
          if (inputCompare > 0) {
            comparePrice = Number(comparePrice) + inputCompare * inputQty + comboCompareAddon;
          } else {
            comparePrice = Number(comparePrice) + inputPrice * inputQty + comboCompareAddon;
          }
          if (isMainVariantInput) {
            selectedVariantPrice = Number(selectedVariantPrice) + inputPrice * inputQty + comboAddon;
            if (inputCompare > 0) {
              selectedVariantComparePrice = Number(selectedVariantComparePrice) + inputCompare * inputQty + comboCompareAddon;
            }
          }
          if (!isSubscriptionInput) {
            variantOnlyPrice = Number(variantOnlyPrice) + inputPrice * inputQty + comboAddon;
            if (inputCompare > 0) {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputCompare * inputQty + comboCompareAddon;
            } else {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputPrice * inputQty + comboCompareAddon;
            }
          }
        } else {
          price = Number(price) + inputPrice + comboAddon;
          if (inputCompare > 0) {
            comparePrice = Number(comparePrice) + inputCompare + comboCompareAddon;
          } else {
            comparePrice = Number(comparePrice) + inputPrice + comboCompareAddon;
          }
          if (isMainVariantInput) {
            selectedVariantPrice = Number(selectedVariantPrice) + inputPrice + comboAddon;
            if (inputCompare > 0) {
              selectedVariantComparePrice = Number(selectedVariantComparePrice) + inputCompare + comboCompareAddon;
            }
          }
          if (!isSubscriptionInput) {
            variantOnlyPrice = Number(variantOnlyPrice) + inputPrice + comboAddon;
            if (inputCompare > 0) {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputCompare + comboCompareAddon;
            } else {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputPrice + comboCompareAddon;
            }
          }
        }
        if (input.dataset.sellingPlan) {
          const spLinePrice = inputPrice * inputQty;
          const spLineCombo = comboAddon;
          sellingPlanMainOnly = spLinePrice;
          sellingPlanComboOnly = spLineCombo;
          sellingPlan = (sellingPlan || 0) + spLinePrice + spLineCombo;

          const isAddl = input.dataset.additionalSubInput !== undefined;
          const isDoubleUp = input.dataset.doubleUpInput !== undefined;
          let spLabel = input.dataset.deliveryInterval || '';
          if (!spLabel) {
            const parentBlock = input.closest('.subscription-options-block');
            if (parentBlock) spLabel = parentBlock.dataset.mainDeliveryInterval || parentBlock.dataset.recurrenceLabel || '';
          }
          sellingPlanLines.push({ price: spLinePrice + spLineCombo, label: spLabel, isAdditional: isAddl, isDoubleUp: isDoubleUp });
        }
      }
    }

    // selling_plan_only mode: subscription radios don't carry data-product-selection,
    // so manually integrate the subscription discount into pricing
    const subBlock = document.querySelector('.subscription-options-block');
    if (subBlock && subBlock.dataset.sellingPlanOnly === 'true') {
      const buyPlanChecked = subBlock.querySelector('input[name="selling_plan_option"]:checked');
      const isBuyOnce = buyPlanChecked && buyPlanChecked.id.includes('buy_once');
      const hideNoSub = subBlock.dataset.hideNoSub === 'true';

      if (buyPlanChecked && !isBuyOnce) {
        const discountEl = subBlock.querySelector('[data-discount]');
        if (discountEl) {
          const multiplier = parseFloat(discountEl.dataset.discount);
          if (multiplier > 0 && multiplier < 1) {
            const rawVariantPrice = variantOnlyPrice;
            const rawComparePrice = variantOnlyComparePrice;
            const discountedPrice = Math.round(rawVariantPrice * multiplier);

            price = discountedPrice;
            comparePrice = rawVariantPrice > discountedPrice ? rawVariantPrice : rawComparePrice;
            sellingPlan = discountedPrice;

            if (hideNoSub) {
              variantOnlyPrice = discountedPrice;
              variantOnlyComparePrice = rawVariantPrice > discountedPrice ? rawVariantPrice : rawComparePrice;
            }
          }
        }
      }
    }

    // When main product is skipped (subscription replaces it), use subscription price
    // for the price/reviews display so it doesn't show zero
    if (variantOnlyPrice === 0 && price > 0) {
      variantOnlyPrice = price;
      variantOnlyComparePrice = comparePrice;
    }

    const hasVariantCompareSavings = selectedVariantComparePrice > 0 && selectedVariantComparePrice > selectedVariantPrice;

    if (document.querySelector("[data-product-price-money]")) {
      const priceMoneyEl = document.querySelector("[data-product-price-money]");
      const finalPrice = Shopify.formatMoney(price, document.body.getAttribute("data-money-format"));
      const hasCompareSavings = comparePrice > 0 && comparePrice > price;
      const finalComparePrice = hasCompareSavings ? " <span>" + Shopify.formatMoney(comparePrice, document.body.getAttribute("data-money-format")) + "</span>" : "";
      const sectionRecurrence = priceMoneyEl.dataset.recurrenceLabel || '';
      const recurrenceSuffix = sectionRecurrence ? ` <span class="total-recurrence">${sectionRecurrence}</span>` : '';
      
      // Check if all options are selected by looking at the variant input
      const variantInput = document.querySelector("#selected-variant-id");
      const allOptionsSelected = variantInput && !variantInput.hasAttribute("data-options-unselected");
      
      // Check if we need to replace "Pending" state
      const isPending = priceMoneyEl.hasAttribute("data-pending-until-selected");
      
      if (isPending) {
        if (allOptionsSelected && price > 0) {
          // All options selected - replace pending state with actual price
          priceMoneyEl.innerHTML = `<span>${finalPrice}</span>${finalComparePrice}${recurrenceSuffix}`;
          priceMoneyEl.removeAttribute("data-pending-until-selected");
          
          // Show savings breakdown if hidden
          const savingsBreakdown = document.querySelector("[data-show-when-selected]");
          if (savingsBreakdown) {
            savingsBreakdown.style.display = hasVariantCompareSavings ? "" : "none";
            savingsBreakdown.removeAttribute("data-show-when-selected");
          }
          
          // Show compare price if hidden
          const comparePriceEl = priceMoneyEl.querySelector(".compare-price-value");
          if (comparePriceEl) {
            comparePriceEl.style.display = "";
          }
        }
        // If not all options selected, keep showing "Pending"
      } else {
        // Normal price update (not in pending state)
        priceMoneyEl.innerHTML = `<span>${finalPrice}</span>${finalComparePrice}${recurrenceSuffix}`;
      }
    }

    // Update savings breakdown if present (for bundle products)
    if (document.querySelector("[data-total-value]") && document.querySelector("[data-savings-value]")) {
      const totalValueEl = document.querySelector("[data-total-value]");
      const savingsValueEl = document.querySelector("[data-savings-value]");
      const savingsBreakdownEl = totalValueEl ? totalValueEl.closest(".savings-breakdown") : null;
      
      if (hasVariantCompareSavings && comparePrice > 0 && comparePrice > price) {
        const savings = comparePrice - price;
        totalValueEl.textContent = Shopify.formatMoney(comparePrice, document.body.getAttribute("data-money-format"));
        savingsValueEl.textContent = "-" + Shopify.formatMoney(savings, document.body.getAttribute("data-money-format"));
        if (savingsBreakdownEl) {
          savingsBreakdownEl.style.display = "";
        }
      } else if (savingsBreakdownEl) {
        savingsBreakdownEl.style.display = "none";
      }
    }

    if (document.querySelector("[data-product-price-text]")) {
      document.querySelector("[data-product-price-text]").dataset.klarnaPrice = price;
    }

    const liquidBreakdown = document.querySelector('[data-recurrence-breakdown]');
    if (liquidBreakdown) liquidBreakdown.style.display = 'none';

    if (sellingPlan && document.querySelector("[data-product-price-text]")) {
      const moneyFmt = document.body.getAttribute("data-money-format");
      const subBlockForLabel = document.querySelector('.subscription-options-block');
      const mainInterval = subBlockForLabel ? (subBlockForLabel.dataset.mainDeliveryInterval || '') : '';
      const comboInterval = subBlockForLabel ? (subBlockForLabel.dataset.comboDeliveryInterval || '') : '';
      const hasCombo = subBlockForLabel && subBlockForLabel.dataset.hasCombo === 'true' && sellingPlanComboOnly > 0;

      if (sellingPlanLines.length > 1) {
        let linesHtml = '';
        sellingPlanLines.forEach((line) => {
          let lineLabel = line.label || mainInterval || (subBlockForLabel ? subBlockForLabel.dataset.recurrenceLabel : '') || 'every 6 months';
          const linePrice = Shopify.formatMoney(line.price, moneyFmt);
          linesHtml += `<span class="total-recurrence-line-js">+ ${linePrice} ${lineLabel.toLowerCase()}</span>`;
        });
        document.querySelector("[data-product-price-text]").innerHTML =
          `Your total today<span class="sub total-recurrence-breakdown-js">${linesHtml}</span>`;
      } else if (hasCombo && mainInterval) {
        const mainPrice = Shopify.formatMoney(sellingPlanMainOnly, moneyFmt);
        const comboPrice = Shopify.formatMoney(sellingPlanComboOnly, moneyFmt);
        const comboLabel = comboInterval || mainInterval;
        document.querySelector("[data-product-price-text]").innerHTML =
          `Your total today<span class="sub total-recurrence-breakdown-js">` +
          `<span class="total-recurrence-line-js">+ ${mainPrice} ${mainInterval.toLowerCase()}</span>` +
          `<span class="total-recurrence-line-js">+ ${comboPrice} ${comboLabel.toLowerCase()}</span>` +
          `</span>`;
      } else {
        const subscriptionPrice = Shopify.formatMoney(sellingPlan, moneyFmt);
        let recurrenceLabel = mainInterval || '';
        if (!recurrenceLabel && subBlockForLabel) {
          recurrenceLabel = subBlockForLabel.dataset.recurrenceLabel || '';
        }
        if (!recurrenceLabel) recurrenceLabel = 'every 6 months';
        document.querySelector("[data-product-price-text]").innerHTML = `Your total today <span class="sub">+ ${subscriptionPrice} ${recurrenceLabel.toLowerCase()}</span>`;
      }
    } else if (document.querySelector("[data-product-price-text]")) {
      const hasSavingsBreakdown = !!document.querySelector(".savings-breakdown");
      document.querySelector("[data-product-price-text]").innerHTML = hasSavingsBreakdown && hasVariantCompareSavings ? `Total Price` : `Your total`;
    }

    // update global-product__price price
    if (document.querySelector(".global-product__price")) {
      const priceEl = document.querySelector(".global-product__price");
      const mirrorTotal = priceEl.hasAttribute('data-mirror-total');
      // Use full price if mirror_total is on, or if variant-only price is 0 (e.g. head options removed the main variant input)
      const useFullPrice = mirrorTotal || (variantOnlyPrice === 0 && price > 0);
      const displayPrice = useFullPrice ? price : variantOnlyPrice;
      const displayComparePrice = useFullPrice ? comparePrice : variantOnlyComparePrice;
      const saleLabel = priceEl.querySelector(".slabel");
      const compareAtPriceEl = priceEl.querySelector(".compare-at-price");
      const salePriceWrapper = priceEl.querySelector(".sale_price-wrapper");
      const priceMoney = Shopify.formatMoney(displayPrice, document.body.getAttribute("data-money-format"));
      const compareAtMoney = displayComparePrice > 0 && displayComparePrice > displayPrice ? Shopify.formatMoney(displayComparePrice, document.body.getAttribute("data-money-format")) : null;

      if(priceEl.querySelector(".from_text")) {
        const fromText = priceEl.querySelector(".from_text");
        const variantInput = document.querySelector("#selected-variant-id");
        const hasSelection = variantInput && !variantInput.hasAttribute("data-options-unselected");
        fromText.style.display = (hasSelection || displayPrice === 0) ? "none" : "";
      }

      if (compareAtPriceEl && compareAtMoney) {
        compareAtPriceEl.textContent = compareAtMoney;
        compareAtPriceEl.style.display = "";
      } else if (compareAtPriceEl) {
        compareAtPriceEl.style.display = "none";
      }

      // Find price element using multiple selectors for compatibility
      let priceSaleEl = priceEl.querySelector(".price--sale") || 
                        priceEl.querySelector("span[data-ig-pricetext]") ||
                        priceEl.querySelector("span[data-price]") ||
                        priceEl.querySelector("div.price > span.price");
      
      if (priceSaleEl) {
        priceSaleEl.textContent = priceMoney;
        if (displayComparePrice > displayPrice && displayComparePrice > 0) {
          priceSaleEl.classList.add('price--sale');
        } else {
          priceSaleEl.classList.remove('price--sale');
        }
      }

      if (saleLabel && displayComparePrice > displayPrice && displayComparePrice > 0) {
        const percentOff = Math.round(((displayComparePrice - displayPrice) / displayComparePrice) * 100);
        saleLabel.textContent = `${percentOff}% OFF`;
        saleLabel.style.display = "";
      } else if (saleLabel) {
        saleLabel.style.display = "none";
      }

      if (salePriceWrapper) {
        if (displayComparePrice > displayPrice && displayComparePrice > 0) {
          salePriceWrapper.style.display = "";
        } else {
          salePriceWrapper.style.display = "none";
        }
      }

      // Update alternative style price display (price-reviews-container--alt)
      const altPriceContainer = priceEl.querySelector(".price-reviews-container--alt");
      if (altPriceContainer) {
        const comparePriceEl = altPriceContainer.querySelector(".compare-price");
        const currentPriceEl = altPriceContainer.querySelector(".current-price");
        const savingsBadgeEl = altPriceContainer.querySelector(".savings-badge");
        
        if (currentPriceEl) {
          const nowText = currentPriceEl.textContent.includes('Now') ? 'Now ' : '';
          currentPriceEl.textContent = nowText + priceMoney;
        }
        
        if (comparePriceEl && compareAtMoney) {
          const worthText = comparePriceEl.textContent.includes('Worth') ? 'Worth ' : '';
          comparePriceEl.textContent = worthText + compareAtMoney;
          comparePriceEl.style.display = "";
        } else if (comparePriceEl) {
          comparePriceEl.style.display = "none";
        }
        
        if (savingsBadgeEl && displayComparePrice > displayPrice && displayComparePrice > 0) {
          const savings = displayComparePrice - displayPrice;
          const savingsMoney = Shopify.formatMoney(savings, document.body.getAttribute("data-money-format"));
          const savingsClean = savingsMoney.replace(/\.00$/, '');
          const extraSavingsText = savingsBadgeEl.dataset.savingsBadgeExtraText ? ' ' + savingsBadgeEl.dataset.savingsBadgeExtraText : '';
          savingsBadgeEl.textContent = 'Save ' + savingsClean + extraSavingsText;
          savingsBadgeEl.style.display = "";
        } else if (savingsBadgeEl) {
          savingsBadgeEl.style.display = "none";
        }
      }
    }

    this.updateKlarnaPrice();

    if(document.querySelector(".global-product .additional_payment_container") && document.querySelector(".global-product klarna-placement")) {
      const targetContainer = document.querySelector(".global-product .additional_payment_container");
      const klarnaEl = document.querySelector(".global-product klarna-placement");
      if (sellingPlan) {
        targetContainer.classList.add("hidden");
        klarnaEl.classList.add("hidden");
      } else {
        targetContainer.classList.remove("hidden");
        klarnaEl.classList.remove("hidden");
      }
    }

    // Sync subscription block per-variant price divs with the active variant
    const _checkedSel = document.querySelector('[data-product-selection]:checked');
    const _hiddenId = document.querySelector('input[name="id"]');
    const _activeVid = (_checkedSel && _checkedSel.value) || (_hiddenId && _hiddenId.value) || (document.getElementById('selected-variant-id') || {}).value;
    if (_activeVid) {
      document.querySelectorAll('.subscription-options-block .price[data-variant-id], .subscription-options-block [data-plan-row-price] [data-variant-id]').forEach(function(el) {
        el.style.display = el.dataset.variantId === String(_activeVid) ? '' : 'none';
      });
    }
  },

  // Watch for changes in variant selection and update gallery
  watchSwappers() {
    const variantSelectors = document.querySelectorAll("[data-swapper]");
    const swappableElements = document.querySelectorAll("[data-colour-swap]");

    if (variantSelectors.length === 0) {
      console.warn("No variant swappers found.");
      return;
    }

    variantSelectors.forEach((swapper) => {
      swapper.addEventListener("click", (event) => {
        const newVariant = event.target.dataset.swapper;
        this.updateGallerySwatches(event.target.dataset.swapper);
        
        if (swappableElements.length > 0) {
          for (const el of swappableElements) {
            if (el.dataset.colourSwap === newVariant) {
              el.classList.add("active");
            } else {
              el.classList.remove("active");
            }
          }
          
          // Handle color option inputs
          const inputs = document.querySelectorAll("product input");
          for (const input of inputs) {
            if (input.dataset.colour) {
              if (input.dataset.colour === newVariant && input.dataset.defaultOption == "true") {
                input.checked = true;
              }
            }
          }
          
          // Update bundle swatches with new color's variant data
          setTimeout(() => {
            // Update each bundle swatch with the new color's variant data
            const bundleInputs = document.querySelectorAll("input[name='variant'][data-variant-map]");
            bundleInputs.forEach(input => {
              try {
                const variantMap = JSON.parse(input.dataset.variantMap);
                const colorData = variantMap[newVariant];
                
                if (colorData) {
                  // Update input attributes
                  input.value = colorData.id;
                  input.dataset.variantId = colorData.id;
                  input.dataset.igDatatags = colorData.id;
                  input.dataset.price = colorData.price;
                  input.dataset.comparePrice = colorData.compare_price || colorData.price;
                  input.dataset.available = colorData.available;
                  
                  if (colorData.available) {
                    input.removeAttribute('data-out-of-stock');
                  } else {
                    input.dataset.outOfStock = "true";
                  }
                  
                  // If this input is checked, update the hidden variant ID input and refresh payment buttons
                  if (input.checked) {
                    const hiddenVariantInput = document.querySelector("input[name='id']");
                    if (hiddenVariantInput) {
                      hiddenVariantInput.value = colorData.id;
                      // Trigger change event to notify Shopify's dynamic checkout buttons
                      hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                  
                  // Find the label - it's a sibling, not a parent
                  const label = document.querySelector(`label[for="${input.id}"]`);
                  if (label) {
                    // Update image
                    const img = label.querySelector('[data-bundle-image]');
                    if (img && colorData.image) {
                      // Force image reload by setting src with cache buster
                      img.src = colorData.image + '?t=' + Date.now();
                      // Also update srcset if it exists
                      if (img.srcset) {
                        img.srcset = '';
                      }
                    }
                    
                    // Update price displays
                    const priceEl = label.querySelector('[data-bundle-price]');
                    if (priceEl) {
                      priceEl.textContent = Shopify.formatMoney(colorData.price, document.body.getAttribute("data-money-format"));
                      priceEl.dataset.igPricetext = colorData.id;
                    }
                    
                    const comparePriceEl = label.querySelector('[data-bundle-compare-price]');
                    if (comparePriceEl) {
                      if (colorData.compare_price && colorData.compare_price != colorData.price) {
                        comparePriceEl.textContent = Shopify.formatMoney(colorData.compare_price, document.body.getAttribute("data-money-format"));
                        comparePriceEl.dataset.igComparetext = colorData.id;
                        comparePriceEl.dataset.variantId = colorData.id;
                        comparePriceEl.style.display = '';
                      } else {
                        comparePriceEl.style.display = 'none';
                      }
                    }
                    
                    // Update savings label
                    const savingsEl = label.querySelector('[data-bundle-savings]');
                    if (savingsEl && colorData.compare_price && colorData.compare_price != colorData.price) {
                      const savings = Math.round(((colorData.compare_price - colorData.price) / colorData.compare_price) * 100);
                      savingsEl.textContent = `${savings}% OFF`;
                      savingsEl.dataset.igSavingstext = colorData.id;
                      savingsEl.dataset.variantId = colorData.id;
                      savingsEl.style.display = '';
                    } else if (savingsEl) {
                      savingsEl.style.display = 'none';
                    }
                    
                    // Update out of stock label
                    const oosLabel = label.querySelector('[data-bundle-oos-label]');
                    if (oosLabel) {
                      oosLabel.style.display = colorData.available ? 'none' : '';
                    }
                    
                    // Update label classes
                    if (colorData.available) {
                      label.classList.remove('out-of-stock');
                    } else {
                      label.classList.add('out-of-stock');
                    }
                  }
                }
              } catch (e) {
                console.error('Error updating bundle swatch:', e);
              }
            });
            
            // Update data-sticky-selection attributes for all selected steps
            const stickyElements = document.querySelectorAll("[data-sticky-selection]");
            stickyElements.forEach((stickyEl) => {
              const hasSelection = stickyEl.querySelector("input:checked");
              if (hasSelection) {
                stickyEl.setAttribute("data-selection", "selected");
                stickyEl.setAttribute("data-sticky-selection", "selected");
              }
            });
            
            // Update sticky add to cart button
            if (this.updateFloatingATC) {
              this.updateFloatingATC();
            }
            // Re-check ATC disabled state after swatch updates
            this.outOfStockHandler(false);
            
            // Update tray button text if product instance exists
            if (window.PXUTheme && window.PXUTheme.productSectionInstance && window.PXUTheme.productSectionInstance.updateTrayButton) {
              window.PXUTheme.productSectionInstance.updateTrayButton();
            }
          }, 150);
        } else {
          console.warn("No swappable elements found.");
        }
      });
    });
  },

  updateGallerySwatches(swapper) {
    if (document.querySelector(".gallery-swatches .gallery-swatch")) {
      if (document.querySelector(`.gallery-swatches .gallery-swatch.active`)) document.querySelector(`.gallery-swatches .gallery-swatch.active`).classList.remove("active");
      document.querySelector(`.gallery-swatches .gallery-swatch.swatch-${swapper}`).classList.add("active");
    }
  },

  hasUvCaseSelection(scope) {
    const searchScope = scope || document;
    const variantForm = searchScope.querySelector("#variant-options") || searchScope;
    const selectedInputs = variantForm.querySelectorAll('input[type="radio"]:checked');
    const uvCaseRegex = /uv\s*case/i;

    for (const input of selectedInputs) {
      const candidates = [
        input.dataset.bundleOption,
        input.dataset.optionValue,
        input.dataset.title,
        input.value,
      ];

      if (candidates.some((value) => uvCaseRegex.test(String(value || "")))) {
        return true;
      }
    }

    return false;
  },

  hasActivePaidSubscriptionSelection(scope) {
    const searchScope = scope || document;
    const enabledSubscriptionBlocks = searchScope.querySelectorAll(
      '.subscription-options-block[data-convert-subscription-atc-enabled="true"]'
    );

    if (!enabledSubscriptionBlocks.length) {
      return false;
    }

    for (const block of enabledSubscriptionBlocks) {
      const candidates = block.querySelectorAll(
        'input[name="selling_plan_option"]:checked, input[name="subscription-plan"]:checked, input[name="product-subscription-option"]:checked'
      );

      for (const input of candidates) {
        const id = input.id || "";
        const value = (input.value || "").trim();

        if (id.endsWith("buy_once") || id.endsWith("no-sub") || id.endsWith("no_sub")) {
          continue;
        }

        const label = input.closest("label") || (input.id ? block.querySelector('label[for="' + input.id + '"]') : null);
        if (label && (label.classList.contains("no-subscription-option") || label.closest(".no-subscription-option"))) {
          continue;
        }

        if (input.name === "product-subscription-option" && value === "") {
          continue;
        }

        return true;
      }
    }

    return false;
  },

  triggerConvertEvent(eventKey) {
    if (!window.SuriConvertTracking || typeof window.SuriConvertTracking.trigger !== "function") {
      return;
    }

    window.SuriConvertTracking.trigger(eventKey);
  },

  watchATCs() {
    const addToCartButtons = document.querySelectorAll("[data-product-atc]");

    for (const btn of addToCartButtons) {
      // Remove previous click handlers to avoid duplicates
      btn.replaceWith(btn.cloneNode(true));
    }

    const freshButtons = document.querySelectorAll("[data-product-atc]");
    for (const btn of freshButtons) {
      btn.addEventListener("click", async (event) => {
        // If button is disabled / OOS, toggle Back in Stock widget instead of attempting add to cart
        const isDisabled = btn.classList.contains("disabled") || btn.getAttribute("aria-disabled") === "true";
        if (isDisabled) {
          // Keep original behavior: do nothing when OOS is clicked
          return;
        }

        btn.classList.add("loading");

        let items = [];
        let addProducts = true;
        let sellingPlanParent = btn.dataset.sellingPlan;
        let parent = document;

        if(btn.closest(".global-product")) {
          parent = btn.closest(".global-product");
        }

        // Always get the latest quantity
        const quantity = parseInt(btn.getAttribute("data-quantity"), 10) || 1;

        // Loop through all products and build the cart items
        if (parent.querySelector(".global-product product")) {
          const products = parent.querySelectorAll(".global-product product");
          for (const p of products) {
            // Skip main product when subscription replaces it
            if (p.dataset.skipForSubscription === "true") continue;

            const variant = p.querySelector("[data-product-selection]:checked");

            let allowEmpty = false;
            if (p.dataset.allowEmpty) {
              allowEmpty = true;
            }

            // Skip if no variant is selected
            if (!variant && !allowEmpty) {
              p.scrollIntoView({ behavior: "smooth", block: "center" });
              p.classList.add("highlight");
              setTimeout(() => p.classList.remove("highlight"), 1000);
              addProducts = false;
              btn.classList.remove("loading");
              return;
            }

            if (variant) {
              let ids = variant.value.split(",");
              let sellingPlans = [];
              let sellingPlan = null;

              // Handle selling plans as a comma-separated list, synced with ids
              if (!sellingPlanParent) {
                if (variant.dataset.sellingPlan) {
                  sellingPlans = variant.dataset.sellingPlan.split(",");
                }
              } else {
                if (variant.dataset.sellingPlan) {
                  sellingPlans = variant.dataset.sellingPlan.split(",");
                } else {
                  sellingPlans = sellingPlanParent.split(",");
                }
              }

              // Handle quantities as a comma-separated list, synced with ids
              let quantities = [];
              if (variant.dataset.quantity) {
                quantities = variant.dataset.quantity.split(",");
              } else {
                // fallback to 1 for each id
                quantities = Array(ids.length).fill("1");
              }

              // If sellingPlans length matches ids, sync them up, else fallback to single sellingPlan for all
              let useSyncedSellingPlans = sellingPlans.length === ids.length;
              // If quantities length matches ids, sync them up, else fallback to single quantity for all
              let useSyncedQuantities = quantities.length === ids.length;

              // Check if the URL has the ?FREEWB parameter
              const urlParams = new URLSearchParams(window.location.search);
              const freeWBParam = urlParams.get("FREEWB");

              // If the parameter is set, store a cookie
              if (freeWBParam) {
                document.cookie = "FREEWB=true; path=/; max-age=" + 60 * 60 * 24 * 7; // Cookie valid for 7 days
              }

              // Check if the cookie is set
              const cookies = document.cookie.split("; ").reduce((acc, cookie) => {
                const [key, value] = cookie.split("=");
                acc[key] = value;
                return acc;
              }, {});

              if (freeWBParam || cookies.FREEWB) {
                // Only check the first id for upsell logic
                const product = window.allProductUpsells.find((item) => item.id == ids[0]) ?? false;
                if (product && product.url.includes("suri-sustainable-sonic-toothbrush-uv-c-led-case")) {
                  const cartResponse = await fetch("/cart.js", {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                  });

                  if (cartResponse.ok) {
                    const cartData = await cartResponse.json();
                    const isItemInCart = cartData.items.some((item) => item.id === 55422926815611);

                    if (!isItemInCart) {
                      items.push({
                        id: 55422926815611,
                        quantity: 1,
                      });
                    }
                  }
                }
              }

              // Detect addon subscription items — attach _addon_parent_variant
              // so the cart can link this subscription to the parent product variant
              const isAddonSubscription = p.closest('[data-is-addon="true"][data-mode="add_item"]');
              let addonParentVariantId = null;
              if (isAddonSubscription) {
                const mainVariantInput = parent.querySelector('input[name="id"]') || document.getElementById('selected-variant-id');
                if (mainVariantInput) addonParentVariantId = mainVariantInput.value;
              }

              for (let i = 0; i < ids.length; i++) {
                const id = ids[i];
                if (id == "false") {
                  continue;
                }
                let itemSellingPlan = null;
                if (useSyncedSellingPlans) {
                  itemSellingPlan = sellingPlans[i] && sellingPlans[i] !== "null" && sellingPlans[i] !== "" ? sellingPlans[i] : null;
                } else if (sellingPlans.length > 0 && sellingPlans[0] !== "null" && sellingPlans[0] !== "") {
                  itemSellingPlan = sellingPlans[0];
                }
                let itemQuantity = 1;
                if (useSyncedQuantities) {
                  itemQuantity = parseInt(quantities[i], 10) || 1;
                } else if (quantities.length > 0 && quantities[0] !== "") {
                  itemQuantity = parseInt(quantities[0], 10) || 1;
                }
                const itemEntry = {
                  id,
                  quantity: itemQuantity,
                  ...(itemSellingPlan ? { selling_plan: itemSellingPlan } : {}),
                };
                if (addonParentVariantId && itemSellingPlan) {
                  itemEntry.properties = { _addon_parent_variant: addonParentVariantId };
                }
                items.push(itemEntry);
              }
            }
          }
        } else {
          const variantId = btn.dataset.defaultProduct;
          items.push({
            id: variantId,
            quantity: quantity,
          });
        }

        // Pick up extra subscription inputs (double-up, additional offer)
        // These are checked inputs that weren't the "first" per <product> element
        if (addProducts && parent.querySelector(".global-product product")) {
          const processedInputs = new Set();
          parent.querySelectorAll('.global-product product').forEach(p => {
            const first = p.querySelector('[data-product-selection]:checked');
            if (first) processedInputs.add(first);
          });
          const allChecked = parent.querySelectorAll('[data-product-selection]:checked');
          allChecked.forEach(input => {
            if (processedInputs.has(input)) return;
            if (!input.value || input.value === 'false') return;
            const spIds = (input.dataset.sellingPlan || '').split(',').filter(Boolean);
            const variantIds = input.value.split(',');
            const qtys = (input.dataset.quantity || '1').split(',');
            for (let i = 0; i < variantIds.length; i++) {
              if (!variantIds[i] || variantIds[i] === 'false') continue;
              const entry = { id: variantIds[i], quantity: parseInt(qtys[i] || qtys[0] || '1', 10) };
              if (spIds[i] && spIds[i] !== 'null') entry.selling_plan = spIds[i];
              else if (spIds[0] && spIds[0] !== 'null') entry.selling_plan = spIds[0];
              items.push(entry);
            }
          });
        }

        if (items.length === 0 || addProducts == false) return; // Exit if no items to add

        const shouldTrackUvCaseAtc = btn.dataset.convertUvAtcEnabled === "true" && this.hasUvCaseSelection(parent);
        if (shouldTrackUvCaseAtc) {
          this.triggerConvertEvent("atc_uv_case");
        }

        const shouldTrackSubscriptionAtc = this.hasActivePaidSubscriptionSelection(parent);
        if (shouldTrackSubscriptionAtc) {
          this.triggerConvertEvent("atc_subscription");
        }

        try {
          // Add items to cart
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) throw new Error("Failed to add to cart");

          window.PXUTheme.jsCart.updateCart();
          window.PXUTheme.jsCart.toggleCart("show");

          btn.classList.remove("loading");
          this.reloadProductForm();
        } catch (error) {
          console.error("Error adding to cart:", error);
          btn.classList.remove("loading");
        }
      });
    }
  },

  clearInputs() {
    const allInputs = document.querySelectorAll("product input:checked");

    for (const input of allInputs) {
      if (input.dataset.defaultOption === "true") continue;
      input.checked = false;

      if (input.dataset.colour && input.closest("product")) {
        const product = input.closest("product");
        const colour = input.dataset.colour;
        if (product.querySelector(`input[data-colour="${colour}"][data-default-option]`)) {
          const defaultInput = product.querySelector(`input[data-colour="${colour}"][data-default-option]`);
          defaultInput.checked = true;
        }
      }
    }

    // Note: p_global_subscription resets itself via the 'productSelectionsCleared' event

    if (document.querySelector("[data-default-price]")) {
      for (const priceEl of document.querySelectorAll("[data-default-price]")) {
        if (priceEl.dataset.defaultPrice) {
          priceEl.textContent = priceEl.dataset.defaultPrice;
        }
      }
    }
    
    // Reset subscription activation flag so it preselects again on next selection
    this._subscriptionActivated = false;
    
    // Reset user preferences so defaults can apply again
    window.subscriptionPreference = null;
    
    // Clear "Also Included" selected state
    const alsoIncludedItems = document.querySelectorAll('[data-also-included-item]');
    alsoIncludedItems.forEach(item => {
      item.classList.remove('selected');
      item.classList.remove('active');
    });
    
    // Reset the also included heading selection state
    const alsoIncludedHeading = document.querySelector('.global-product__addons .heading[data-sticky-selection]');
    if (alsoIncludedHeading) {
      alsoIncludedHeading.setAttribute('data-selection', '');
      alsoIncludedHeading.setAttribute('data-sticky-selection', '');
    }
    
    // Set flag to prevent subscription preselection during reset
    window._resettingSelections = true;
    
    // Mark variant input as unselected initially (will be updated by resetToDefaults if colors are re-selected)
    const variantInput = document.getElementById('selected-variant-id');
    if (variantInput) {
      variantInput.setAttribute('data-options-unselected', '');
    }
    
    // Also clear color radio buttons in variant-options form
    const variantOptionsForm = document.getElementById('variant-options');
    if (variantOptionsForm) {
      const colorInputs = variantOptionsForm.querySelectorAll('input[type="radio"][data-swapper]');
      colorInputs.forEach(input => {
        input.checked = false;
      });
      
      // Reset the variant options groups to initial progressive disclosure state
      const optionGroups = variantOptionsForm.querySelectorAll('.product_variant_option');
      optionGroups.forEach((group, index) => {
        if (index === 0) {
          group.classList.add('available');
        } else {
          group.classList.remove('available');
        }
      });
    }
    
    // Dispatch event — SubscriptionBlockController listens and handles its own reset
    document.dispatchEvent(new CustomEvent('productSelectionsCleared', { bubbles: true }));
    
    setTimeout(function() {
      window._resettingSelections = false;
    }, 300);
  },

  captureInitialFormState() {
    const form = document.querySelector('.global-product .global-product__blocks');
    if (form) {
      this._initialFormHTML = form.innerHTML;
    }
  },

  reloadProductForm() {
    const form = document.querySelector('.global-product .global-product__blocks');
    if (!form || !this._initialFormHTML) {
      this.resetFloatingATC();
      this.clearInputs();
      this.disableDisabledBlocks();
      return;
    }

    // Restore the pristine server-rendered HTML
    form.innerHTML = this._initialFormHTML;

    // Reset global state
    window.subscriptionPreference = null;
    window.headOptionsPreference = null;
    window._resettingSelections = false;

    // Re-execute inline scripts so variant-options.liquid and subscription JS re-bind
    form.querySelectorAll('script').forEach(function(oldScript) {
      var newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Re-init all global product handlers on the fresh DOM
    this.onLoadElements();
    this.disableDisabledBlocks();
    this.resetFloatingATC();

    this.watchSwappers();

    if (document.querySelector("[data-product-selection]")) {
      this.watchInputs();
    }
    if (document.querySelector("[data-product-atc]")) {
      this.watchATCs();
    }
    if (document.querySelectorAll("input[data-update-selected-variant]")) {
      this.watchSelectedVariantChanges();
    }

    this.initProgressiveDisclosure();
    this.initSubscriptionBlocks();
    this.outOfStockHandler(false);
    this.positionBackInStockWidget();
    this.updateKlarnaPrice();
    this.watchMainATCTextChanges();
    this.watchOutOfStockTextChanges();

    if (document.querySelectorAll("[data-sticky-selection]") && document.getElementById("getStartedBtn")) {
      this.updateFloatingATC();
    }

    if (document.body.getAttribute("data-intelligems-enabled") === "true") {
      this.initIntelligems();
    }
  },

  watchATCScroll() {
    let currentScroll = window.scrollY;
    let element = document.querySelector(".global-product__atc-bar");

    const checkATCScroll = () => {
      if (window.scrollY > 0) {
        element.classList.remove("scrolled");
      } else {
        element.classList.add("scrolled");
      }

      if (window.scrollY > currentScroll) {
        element.classList.add("show");
      } else {
        element.classList.remove("show");
      }

      currentScroll = window.scrollY;

      const btn = element.querySelector(".btn");
      const productSection = document.querySelector(".global-section-parent.global-product");
      btn.addEventListener("click", (event) => {
        // event.preventDefault();
        document.querySelector("#header").classList.remove("show");
        element.classList.remove("show");
        // productSection.scrollIntoView({ behavior: "smooth" });
      });
    };

    // Run the check immediately on page load
    checkATCScroll();

    // Add the scroll event listener
    document.addEventListener("scroll", checkATCScroll);
  },

  updateFloatingATC() {
    const getStartedBtn = document.getElementById("getStartedBtn");
    if (!getStartedBtn) return;
    const getStartedBtnText = getStartedBtn.querySelector("span.text");

    const addToCartLabel = this.getButtonLabel("addToCart", getStartedBtn.dataset.labelAddToCart || (getStartedBtnText ? getStartedBtnText.textContent.trim() : ""));
    const floatingAddToCartLabel = this.getButtonLabel("floatingAddToCart", addToCartLabel);
    const floatingOutOfStockLabel = this.getButtonLabel("floatingOutOfStock", this.getButtonLabel("outOfStock", getStartedBtn.dataset.labelOutOfStock || ""));
    const floatingDefaultLabel = this.getButtonLabel("floatingDefault", getStartedBtn.dataset.labelDefault || (getStartedBtnText ? getStartedBtnText.textContent.trim() : ""));

    // Helper function to find the current incomplete step
    const findCurrentTarget = () => {
      const selectEls = document.querySelectorAll("[data-sticky-selection]");
      let currentTargetEl = selectEls[0];

      while (currentTargetEl && (currentTargetEl.dataset.stickySelection === "selected" || currentTargetEl.dataset.selection === "selected")) {
        const nextIndex = Array.from(selectEls).indexOf(currentTargetEl) + 1;
        currentTargetEl = selectEls[nextIndex] || null;
      }
      
      return currentTargetEl;
    };

    // Update button text
    const currentTargetEl = findCurrentTarget();
    
    // Check if current step is the ATC button itself (last step) or if all steps are complete
    const isAtcStep = currentTargetEl && currentTargetEl.dataset.stickySelectionTitle === floatingAddToCartLabel;
    
    if (currentTargetEl && !isAtcStep) {
      // There's an incomplete step before the ATC button
      getStartedBtnText.textContent = currentTargetEl.dataset.stickySelectionTitle;
      getStartedBtn.classList.remove("disabled");
      getStartedBtn.setAttribute("aria-disabled", "false");
    } else {
      // All steps are selected - check if the selection is available
      const inputs = document.querySelectorAll("[data-product-selection]");
      let checkedOutOfStock = false;
      let hasOptionsUnselected = false;
      
      for (const input of inputs) {
        if (input.checked) {
          if (input.dataset.outOfStock === "true") {
            checkedOutOfStock = true;
          }
          if (input.hasAttribute("data-options-unselected")) {
            hasOptionsUnselected = true;
          }
        }
      }
      
      // Mirror main ATC button state
      const mainATC = document.querySelector("[data-product-atc]");
      const mainATCOutOfStockLabel = this.getButtonLabel("outOfStock", mainATC?.dataset?.oosLabel || floatingOutOfStockLabel);
      const mainATCDisabled = mainATC && (mainATC.classList.contains("disabled") || (mainATCOutOfStockLabel && mainATC.textContent.trim() === mainATCOutOfStockLabel));
      
      if (hasOptionsUnselected) {
        // Options are not fully selected - find the next unselected option
        const nextUnselected = findCurrentTarget();
        if (nextUnselected) {
          getStartedBtnText.textContent = nextUnselected.dataset.stickySelectionTitle;
          getStartedBtn.classList.remove("disabled");
          getStartedBtn.setAttribute("aria-disabled", "false");
        } else {
          getStartedBtnText.textContent = floatingAddToCartLabel || floatingDefaultLabel;
          getStartedBtn.classList.add("disabled");
          getStartedBtn.setAttribute("aria-disabled", "true");
        }
      } else if (checkedOutOfStock || mainATCDisabled) {
        getStartedBtnText.textContent = floatingOutOfStockLabel || mainATCOutOfStockLabel || floatingAddToCartLabel;
        getStartedBtn.classList.add("disabled");
        getStartedBtn.setAttribute("aria-disabled", "true");
      } else {
        const subLabel = this.getActiveSubscriptionLabel();
        getStartedBtnText.textContent = subLabel || floatingAddToCartLabel || floatingDefaultLabel;
        getStartedBtn.classList.remove("disabled");
        getStartedBtn.setAttribute("aria-disabled", "false");
      }
    }

    // Only add event listeners once
    if (!this._floatingATCInitialized) {
      this._floatingATCInitialized = true;
      
      // Use event delegation on document to handle dynamically loaded elements
      document.addEventListener("click", (e) => {
        const el = e.target.closest("[data-sticky-selection]");
        if (!el) return;
        
        const input = el.querySelector("input:checked, input[selected]");
        if (!input) {
          return false;
        }

        if (el.getAttribute("data-selection") === "selected") {
          return false;
        }

        el.setAttribute("data-selection", "selected");
        el.setAttribute("data-sticky-selection", "selected");

        // Find the next element
        const selectEls = document.querySelectorAll("[data-sticky-selection]");
        const currentIndex = Array.from(selectEls).indexOf(el);
        const nextEl = selectEls[currentIndex + 1];
        
        if (nextEl) {
          // Note: stick to one attribute naming convention
          const nextTitle = nextEl.dataset.stickySelectionTitle || nextEl.dataset.selectionTitle;
          if (nextTitle) {
            getStartedBtnText.textContent = nextTitle;
            getStartedBtn.classList.remove("disabled");
            getStartedBtn.setAttribute("aria-disabled", "false");
          }
        } else {
          // This was the last step - check availability
          const inputs = document.querySelectorAll("[data-product-selection]");
          let checkedOutOfStock = false;
          
          for (const input of inputs) {
            if (input.checked && input.dataset.outOfStock === "true") {
              checkedOutOfStock = true;
              break;
            }
          }
          
          const mainATC = document.querySelector("[data-product-atc]");
          const mainATCOutOfStockLabel = this.getButtonLabel("outOfStock", mainATC?.dataset?.oosLabel || floatingOutOfStockLabel);
          const mainATCDisabled = mainATC && (mainATC.classList.contains("disabled") || (mainATCOutOfStockLabel && mainATC.textContent.trim() === mainATCOutOfStockLabel));
          
          if (checkedOutOfStock || mainATCDisabled) {
            getStartedBtnText.textContent = floatingOutOfStockLabel || mainATCOutOfStockLabel || floatingAddToCartLabel;
            getStartedBtn.classList.add("disabled");
            getStartedBtn.setAttribute("aria-disabled", "true");
          } else {
            getStartedBtnText.textContent = floatingAddToCartLabel || floatingDefaultLabel;
            getStartedBtn.classList.remove("disabled");
            getStartedBtn.setAttribute("aria-disabled", "false");
          }
        }
      });

      getStartedBtn.addEventListener("click", (event) => {
        // event.preventDefault();
        const getStartedText = getStartedBtnText.textContent.trim();

        if (getStartedText === (floatingOutOfStockLabel || this.getButtonLabel("outOfStock"))) {
          // Don't do anything if out of stock
          return;
        }

        if (getStartedText === (floatingAddToCartLabel || addToCartLabel)) {
          const addToCartBtn = document.querySelector("[data-product-atc]");
          if (addToCartBtn) {
            addToCartBtn.click(); // Triggers the native click event
          }
          return; // Don't scroll, just add to cart
        }

        // Find the current incomplete step dynamically
        const targetEl = findCurrentTarget();
        if (!targetEl) return;

        const offset = 51;
        const rect = targetEl.getBoundingClientRect();
        const scrollTop = window.scrollY || window.pageYOffset;
        const targetTop = rect.top + scrollTop - offset;

        const header = document.querySelector("#header");
        let lastScrollTime = Date.now();
        let keepRemoving = true;

        const suppressHeader = () => {
          if (!keepRemoving) return;
          header.classList.remove("show");
          if (Date.now() - lastScrollTime > 500) {
            keepRemoving = false;
            return;
          }
          requestAnimationFrame(suppressHeader);
        };

        const scrollHandler = () => {
          lastScrollTime = Date.now();
          header.classList.remove("show");
        };

        window.addEventListener("scroll", scrollHandler);
        requestAnimationFrame(suppressHeader);

        window.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });

        targetEl.classList.add("Attention");
        setTimeout(() => {
          targetEl.classList.remove("Attention");
        }, 2000);
      });
    }
  },

  resetFloatingATC() {
    const selectEls = document.querySelectorAll("[data-sticky-selection]");
    selectEls.forEach((el) => {
      el.setAttribute("data-sticky-selection", "");
      el.setAttribute("data-selection", "");
    });
    this.updateFloatingATC();
  },

  initIntelligems() {
    const waitForIntelligems = () => {
      return new Promise((resolve) => {
        const checkExist = setInterval(() => {
          if (window.igData && Object.keys(window.igData).length > 0) {
            clearInterval(checkExist);
            resolve();
          }
        }, 100);
      });
    }

    waitForIntelligems().then(() => {
      if (window.igData) {

        // Update data-price and data-compare-price strings where data-ig-datatags is equal to the variant ID 
        const igDataTags = document.querySelectorAll("[data-ig-datatags]");
        
        igDataTags.forEach((tag) => {
          const id = tag.dataset.igDatatags;
          const price = window.igData?.price?.getPriceByVariantId(id) * 100;
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id) * 100;
          if (price && price > 0) {
            tag.setAttribute("data-price", price);
          }
          if (comparePrice && comparePrice > 0) {
            tag.setAttribute("data-compare-price", comparePrice);
          }
        });

        // Update text elements showing prices where the data-ig-pricetext and data-ig-comparetext attributes are set to the variant ID 
        const igPriceTexts = document.querySelectorAll("[data-ig-pricetext]");
        igPriceTexts.forEach((text) => {
          const id = text.dataset.igPricetext;
          const price = window.igData?.price?.getPriceByVariantId(id);
          if (price && price > 0) {
            text.textContent = document.body.getAttribute("data-money-format").replace("{{amount}}", price);
          }
        });

        const igCompareTexts = document.querySelectorAll("[data-ig-comparetext]");
        igCompareTexts.forEach((text) => {
          const id = text.dataset.igComparetext;
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id);
          if (comparePrice && comparePrice > 0) {
            text.textContent = document.body.getAttribute("data-money-format").replace("{{amount}}", comparePrice);
          }
        });

        const igSavingsTexts = document.querySelectorAll("[data-ig-savingstext]");
        igSavingsTexts.forEach((text) => {
          const id = text.dataset.igSavingstext;
          const price = window.igData?.price?.getPriceByVariantId(id);
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id);
          const savings = Math.round(((comparePrice - price) / comparePrice) * 100);
          if (savings && savings > 0) {
            text.textContent = `${savings}% OFF`;
          }
        });

      }
    });
  },

  onLoadElements() {
    document.querySelectorAll('[data-loaded="false"]').forEach(el => {
      el.setAttribute('data-loaded', 'true');
    });
    document.querySelectorAll('.not-loaded').forEach(el => {
      el.classList.remove('not-loaded');
    });
  },

  positionBackInStockWidget() {
    const bis = document.getElementById('backInStockWidget');
    if (!bis) return;
    const containers = document.querySelectorAll('.global-product__options');
    const target = containers[containers.length - 1];
    if (target && bis.parentElement !== target) {
      target.appendChild(bis);
    }
  },

  initProgressiveDisclosure() {
    // Handle variant-options progressive disclosure
    const variantOptionsForm = document.getElementById('variant-options');
    if (variantOptionsForm && variantOptionsForm.dataset.progressiveDisclosure === 'true') {
      this.initVariantOptionsDisclosure(variantOptionsForm);
    }

    // Handle toothbrush-options progressive disclosure
    const toothbrushContainers = document.querySelectorAll('.toothbrush-options-block[data-progressive-disclosure="true"]');
    toothbrushContainers.forEach(container => {
      this.initToothbrushOptionsDisclosure(container);
    });

    // Subscription progressive disclosure is handled by SubscriptionBlockController
  },

  initVariantOptionsDisclosure(form) {
    const optionGroups = form.querySelectorAll('.product_variant_option');
    if (!optionGroups.length) return;

    const hasSelection = (group) => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      return Array.from(inputs).some(input => input.checked);
    };

    const updateGroups = () => {
      if (optionGroups.length > 0) {
        optionGroups[0].classList.add('available');
      }

      for (let i = 1; i < optionGroups.length; i++) {
        const previousGroup = optionGroups[i - 1];
        const previousHasSelection = hasSelection(previousGroup);
        
        if (previousHasSelection) {
          optionGroups[i].classList.add('available');
        } else {
          optionGroups[i].classList.remove('available');
        }
      }
    };

    updateGroups();
    setTimeout(updateGroups, 0);

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        updateGroups();
      }
    });
  },

  initToothbrushOptionsDisclosure(container) {
    const optionGroups = container.querySelectorAll('.product-bundle__option');
    if (!optionGroups.length) return;

    const hasSelection = (group) => {
      const inputs = group.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      return Array.from(inputs).some(input => input.checked);
    };

    const updateGroups = () => {
      let allowNext = true;
      optionGroups.forEach((group, index) => {
        if (index === 0) {
          group.classList.add('available');
          allowNext = hasSelection(group);
          return;
        }

        if (allowNext) {
          group.classList.add('available');
          allowNext = hasSelection(group);
        } else {
          group.classList.remove('available');
        }
      });
    };

    updateGroups();
    setTimeout(updateGroups, 0);

    container.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"], input[type="checkbox"]')) {
        updateGroups();
      }
    });

    document.addEventListener('variantColorSelected', updateGroups);
  },

  // initSubscriptionDisclosure and initPreselectedOptions have been replaced
  // by SubscriptionBlockController (managed via initSubscriptionBlocks)

  watchMainATCTextChanges() {
    if (this._mainATCObserver) this._mainATCObserver.disconnect();
    
    const mainATC = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedBtnText = getStartedBtn?.querySelector("span.text");

    if (!mainATC || !getStartedBtn || !getStartedBtnText) return;

    // Store the last known text to avoid unnecessary updates
    let lastMainATCText = mainATC.textContent.trim();

    // Create a MutationObserver to watch for text changes on the main ATC button
    const observer = new MutationObserver((mutations) => {
      const currentText = mainATC.textContent.trim();
      
      // Only update if the text actually changed
      if (currentText !== lastMainATCText) {
        lastMainATCText = currentText;
        
        // Check if we should update the sticky button
        // Only update if all option fieldsets (not including the ATC button itself) are selected
        const optionFieldsets = document.querySelectorAll('[data-sticky-selection]:not([data-product-atc])');
        const allOptionsSelected = Array.from(optionFieldsets).every(fieldset => 
          fieldset.dataset.stickySelection === 'selected'
        );
        
        if (allOptionsSelected) {
          // Update the sticky button text to match the main button
          // But preserve the step-by-step flow for incomplete selections
          const floatingAddToCartLabel = this.getButtonLabel("floatingAddToCart", currentText);
          const floatingDefaultLabel = this.getButtonLabel("floatingDefault", "");
          
          // Only update if the current sticky text is the add to cart label (not a step instruction)
          const currentStickyText = getStartedBtnText.textContent.trim();
          const isShowingATCText = currentStickyText === floatingAddToCartLabel || 
                                   currentStickyText === floatingDefaultLabel ||
                                   currentStickyText === this.buttonLabels.addToCart;
          
          if (isShowingATCText) {
            getStartedBtnText.textContent = currentText;
          }
        }
      }
    });

    observer.observe(mainATC, {
      characterData: true,
      childList: true,
      subtree: true
    });
    this._mainATCObserver = observer;
  },

  watchOutOfStockTextChanges() {
    if (this._oosObservers) {
      this._oosObservers.forEach(o => o.disconnect());
    }
    this._oosObservers = [];
    
    const mainATC = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedBtnText = getStartedBtn?.querySelector("span.text");

    if (!mainATC) return;

    // Helper function to check if text indicates out of stock
    const isOutOfStockText = (text) => {
      if (!text) return false;
      const normalizedText = text.toLowerCase().trim();
      
      // Check for various out of stock patterns (case-insensitive)
      // English variations
      if (normalizedText.includes('out of stock') || 
          normalizedText.includes('out-of-stock') ||
          normalizedText.includes('outofstock')) {
        return true;
      }
      
      // Other language variations (from locale files)
      const oosPatterns = [
        'fora de estoque',      // Portuguese
        '缺货',                  // Chinese
        'en rupture de stock',  // French
        'ausverkauft',          // German
        'agotado',              // Spanish
        'sold out',             // Alternative English
        'unavailable'           // Generic unavailable
      ];
      
      return oosPatterns.some(pattern => normalizedText.includes(pattern));
    };

    // Helper function to disable a button
    const disableButton = (button) => {
      if (button) {
        button.classList.add('disabled');
        button.setAttribute('aria-disabled', 'true');
      }
    };

    // Watch main ATC button
    const mainATCObserver = new MutationObserver(() => {
      const currentText = mainATC.textContent.trim();
      if (isOutOfStockText(currentText)) {
        disableButton(mainATC);
      }
    });

    mainATCObserver.observe(mainATC, {
      characterData: true,
      childList: true,
      subtree: true
    });
    this._oosObservers.push(mainATCObserver);

    if (getStartedBtn && getStartedBtnText) {
      const stickyATCObserver = new MutationObserver(() => {
        const currentText = getStartedBtnText.textContent.trim();
        if (isOutOfStockText(currentText)) {
          disableButton(getStartedBtn);
        }
      });

      stickyATCObserver.observe(getStartedBtnText, {
        characterData: true,
        childList: true,
        subtree: true
      });

      stickyATCObserver.observe(getStartedBtn, {
        characterData: true,
        childList: true,
        subtree: true
      });
      this._oosObservers.push(stickyATCObserver);
    }

    // Also watch and disable additional payment buttons when main ATC is out of stock
    const additionalPaymentContainer = document.querySelector('.additional_payment_container');
    if (additionalPaymentContainer) {
      const paymentObserver = new MutationObserver(() => {
        const currentText = mainATC.textContent.trim();
        if (isOutOfStockText(currentText)) {
          // Hide the additional payment container
          additionalPaymentContainer.classList.add('hidden');
          // Also disable any buttons inside it
          const paymentButtons = additionalPaymentContainer.querySelectorAll('button, [role="button"]');
          paymentButtons.forEach(btn => disableButton(btn));
        }
      });

      paymentObserver.observe(mainATC, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }

    // Initial check on page load
    setTimeout(() => {
      if (mainATC && isOutOfStockText(mainATC.textContent.trim())) {
        disableButton(mainATC);
        if (additionalPaymentContainer) {
          additionalPaymentContainer.classList.add('hidden');
        }
      }
      if (getStartedBtn && getStartedBtnText && isOutOfStockText(getStartedBtnText.textContent.trim())) {
        disableButton(getStartedBtn);
      }
    }, 100);
  }
};

document.addEventListener("DOMContentLoaded", () => {
  window.PXUTheme.jsGlobalProduct.init();
  
  if (document.querySelector(".global-product .shopify-payment-button")) {
    // Get the payment button element
    const paymentButton = document.querySelector(".global-product .shopify-payment-button");
    // Get the target container where the payment button should be moved
    const targetContainer = document.querySelector(".global-product .additional_payment_container");

    // If the container exists, move the payment button into it
    if (targetContainer) {
      targetContainer.appendChild(paymentButton);
    }
  }
  
  // Toggle payment buttons based on initial subscription selection
  // Use a small delay to ensure payment buttons are rendered
  setTimeout(() => {
    const paymentButtons = document.querySelectorAll('.shopify-payment-button, .additional_payment_container');
    if (paymentButtons.length > 0) {
      const checkedInputs = document.querySelectorAll(
        'input[name="subscription-plan"]:checked, input[name="selling_plan_option"]:checked'
      );
      
      let hasSubscription = false;
      checkedInputs.forEach(input => {
        if (input.dataset.sellingPlan) {
          hasSubscription = true;
        }
      });
      
      paymentButtons.forEach(button => {
        if (hasSubscription) {
          button.style.display = 'none';
        } else {
          button.style.display = '';
        }
      });
    }
  }, 500);
});
